import { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Plus,
  Trash2,
  Play,
  Code,
  ChevronDown,
  ChevronUp,
  Sliders,
  CheckSquare,
  Loader2,
} from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getData, postDataWithBody } from "../../../Services/Services";
import {
  getAllColumns,
  getAllurls,
  getInstanceUrl,
  getThemeURL,
} from "../../../Services/EndPoints";
import { ConfirmationModal } from "../../../Components/UiComponents/utils";

const ApiBuilder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryToEdit = location.state?.queryToEdit || null;
  const [instanceData, setInstanceData] = useState(null);

  // Service Configuration States
  const [serviceName, setServiceName] = useState("");
  const [serviceDescription, setServiceDescription] = useState("");
  const [selectedWidgetCategory, setSelectedWidgetCategory] = useState("");

  // API Selection States
  const [selectedApi, setSelectedApi] = useState("");
  const [allUrls, setAllUrls] = useState([]);
  const [loadingApi, setLoadingApi] = useState(false);

  // Query Builder States
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [jsonQuery, setJsonQuery] = useState("");
  const [showQueryBuilder, setShowQueryBuilder] = useState(false);
  const [jsonResponse, setJsonResponse] = useState(null);
  const [isQueryBuilderExpanded, setIsQueryBuilderExpanded] = useState(true);
  const [conditions, setConditions] = useState([]);
  const [orderByFields, setOrderByFields] = useState([]);
  const [isExecuting, setIsExecuting] = useState(false);
  const [compactJsonQuery, setCompactJsonQuery] = useState("");
  const [editId, setEditId] = useState(null);
  const [aggregators, setAggregators] = useState([]);
  const [groupByFields, setGroupByFields] = useState([]);

  // API Response Columns
  const [apiColumns, setApiColumns] = useState([]);
  const [columnDataTypes, setColumnDataTypes] = useState({});

  // Joins functionality - Enhanced with DynamicDashboard logic
  const [joins, setJoins] = useState([]);
  const [joinApiColumns, setJoinApiColumns] = useState({});

  // Add this with your other useState declarations
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: null,
  });

  const Widgets = [
    { display: "Card", value: "CARD" },
    { display: "Chart", value: "CHART" },
    { display: "Map", value: "MAP" },
    { display: "Table", value: "TABLE" },
  ];

  const operatorTypes = [
    "=",
    "!=",
    ">",
    "<",
    ">=",
    "<=",
    "LIKE",
    "NOT LIKE",
    "IN",
    "NOT IN",
    "BETWEEN",
  ];

  const getOperatorsByDataType = (dataType) => {
    if (!dataType) return operatorTypes; // Return all operators if no data type

    const normalizedType = dataType.toLowerCase();

    // String/Text types
    if (
      normalizedType.includes("varchar") ||
      normalizedType.includes("text") ||
      normalizedType.includes("char")
    ) {
      return ["=", "!=", "LIKE", "NOT LIKE", "IN", "NOT IN"];
    }

    // Numeric types (integers, decimals, floats)
    if (
      normalizedType.includes("int") ||
      normalizedType.includes("numeric") ||
      normalizedType.includes("decimal") ||
      normalizedType.includes("float") ||
      normalizedType.includes("double") ||
      normalizedType.includes("bigint")
    ) {
      return ["=", "!=", ">", "<", ">=", "<=", "IN", "NOT IN"];
    }

    // Date/Time types
    if (
      normalizedType.includes("date") ||
      normalizedType.includes("timestamp") ||
      normalizedType.includes("time")
    ) {
      return ["=", "!=", ">", "<", ">=", "<=", "BETWEEN"];
    }

    // Boolean types
    if (normalizedType.includes("boolean") || normalizedType.includes("bool")) {
      return ["=", "!="];
    }

    // Default: return all operators
    return operatorTypes;
  };

  const aggregatorTypes = ["SUM", "MIN", "MAX", "AVG", "COUNT"];
  const joinTypes = ["INNER", "LEFT", "RIGHT"];
  const conditionTypes = ["AND", "OR"];

  // Helper function to extract table name from API URL
  const extractTableNameFromUrl = (url) => {
    if (!url) return "";
    const parts = url.split("/");
    return parts[parts.length - 1];
  };

  // Helper function to get table name by API ID
  const getTableNameByApiId = (apiId) => {
    const apiObj = allUrls.find((api) => String(api.id) === String(apiId));
    return (
      extractTableNameFromUrl(apiObj?.url) || apiObj?.endPointName || apiId
    );
  };

  // Fetch all APIs on component mount
  useEffect(() => {
    fetchAllApis();
    fetchInstance();
  }, []);

  const fetchInstance = async () => {
    try {
      const userProfile = await getData(getThemeURL);

      if (
        !userProfile ||
        !Array.isArray(userProfile) ||
        userProfile.length === 0
      ) {
        console.error("No user profile data found");
        return;
      }

      const user = userProfile[0];
      const roleName = user.role_name;

      let payload;

      if (roleName === "SUPER ADMIN") {
        payload = {
          type: "DEFAULT",
          bank_id: null,
        };
      } else if (user.is_admin === true) {
        payload = {
          type: "HO",
          bank_id: user.bank_id,
        };
      } else {
        payload = {
          type: "U",
          bank_id: user.bank_id,
        };
      }

      const response = await postDataWithBody(getInstanceUrl, payload, {});
      setInstanceData(response);
    } catch (error) {
      console.error("Error fetching instance data:", error);
    }
  };

  const fetchAllApis = async () => {
    try {
      const response = await getData(getAllColumns);
      if (Array.isArray(response)) {
        setAllUrls(response);
      }
    } catch (error) {
      console.error("Error fetching APIs:", error);
      toast.error("Failed to fetch APIs", { position: "top-center" });
    }
  };

  const fetchApiUrlFromGetAllUrls = async (selectedApiId) => {
    try {
      const response = await getData(`${getAllurls}/${selectedApiId}`);
      if (response) {
      } else {
        console.log("No URL found for API ID:", selectedApiId);
      }
    } catch (error) {
      console.error("Error fetching URL for API ID:", selectedApiId, error);
      toast.error("Failed to fetch API URL", { position: "top-center" });
    }
  };

  // Enhanced fetchApiData with auto-selection logic from DynamicDashboard
  const fetchColumnsForJoinApi = async (apiId) => {
    try {
      const selectedApiObj = allUrls.find((api) => String(api.id) === apiId);

      if (!selectedApiObj) {
        console.error("API not found for ID:", apiId);
        return;
      }

      const userProfile = await getData(getThemeURL);

      if (
        !userProfile ||
        !Array.isArray(userProfile) ||
        userProfile.length === 0
      ) {
        console.error("Failed to fetch user profile");
        return;
      }

      const user = userProfile[0];
      const roleName = user.role_name;
      const instance = instanceData[0];
      const fullInstanceUrl = instance.instance_url + selectedApiObj.url;

      let inputobj = {
        instanceUrl: fullInstanceUrl,
        apiType: selectedApiObj.apiType,
      };

      if (roleName === "SUPER ADMIN") {
        inputobj.type = "DEFAULT";
      } else if (user.is_admin === true) {
        inputobj.type = "HO";
        inputobj.bank_id = user.bank_id;
      } else {
        inputobj.type = "U";
        inputobj.bank_id = user.bank_id;
      }

      if (selectedApiObj.apiType === "POST" && selectedApiObj.request) {
        try {
          const requestData = JSON.parse(selectedApiObj.request);
          Object.keys(requestData).forEach((key) => {
            inputobj[key] = requestData[key];
          });
        } catch (parseError) {
          console.error("Error parsing request field:", parseError);
        }
      }

      const response = await fetch("/nifi/external-api", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(inputobj),
      });

      const data = await response.json();

      if (data && data.data && Array.isArray(data.data)) {
        const columns = data.data.map((col) => col.columnName);
        const types = {};
        data.data.forEach((col) => {
          types[col.columnName] = col.dataType || "string";
        });

        setJoinApiColumns((prev) => ({
          ...prev,
          [apiId]: {
            columns,
            dataTypes: types,
          },
        }));

        // Remove auto-selection logic - just show success message
        const tableName = extractTableNameFromUrl(selectedApiObj.url);
        toast.success(`Join API data loaded for ${tableName}`, {
          position: "top-center",
        });
      }
    } catch (error) {
      console.error("Error fetching join API data:", error);
      setJoinApiColumns((prev) => ({
        ...prev,
        [apiId]: {
          columns: [],
          dataTypes: {},
        },
      }));
    }
  };

  const handleApiChange = (e) => {
    const selectedApiId = e.target.value;
    setSelectedApi(selectedApiId);
    setSelectedColumns([]);
    setApiColumns([]);
    setColumnDataTypes({});
    setConditions([]);
    setOrderByFields([]);
    setAggregators([]);
    setGroupByFields([]);
    setJoins([]);
    setJoinApiColumns({});
    setJsonQuery("");
    setJsonResponse(null);

    const selectedApiObj = allUrls.find(
      (api) => String(api.id) === selectedApiId
    );
    if (selectedApiObj) {
      console.log("Selected API:", selectedApiObj.endPointName);
      console.log("API URL:", selectedApiObj.url);
      console.log(
        "Extracted Table Name:",
        extractTableNameFromUrl(selectedApiObj.url)
      );
      fetchApiUrlFromGetAllUrls(selectedApiId);

      // Automatically fetch API data when API is selected
      if (selectedApiId) {
        fetchApiData(selectedApiId, false);
      }
    }
  };

  const fetchApiData = async (apiId = null, isJoinApi = false) => {
    try {
      const targetApiId = apiId || selectedApi;
      setLoadingApi(!isJoinApi);
      if (isJoinApi) {
        // setLoadingColumns(true);
      }

      if (!targetApiId) {
        toast.error("Please select an API", { position: "top-center" });
        return;
      }

      // Add this check to ensure allUrls is available
      if (!allUrls || allUrls.length === 0) {
        console.log("allUrls not ready, waiting...");
        toast.info("Loading API data...", { position: "top-center" });
        return;
      }

      const selectedApiObj = allUrls.find(
        (api) => String(api.id) === String(targetApiId) // Convert both to string for comparison
      );

      if (!selectedApiObj) {
        console.error(
          "API not found. Available APIs:",
          allUrls.map((api) => ({ id: api.id, name: api.endPointName }))
        );
        console.error("Looking for API ID:", targetApiId);
        toast.error("Selected API not found in available APIs", {
          position: "top-center",
        });
        return;
      }

      // Add this check to ensure instanceData is available
      if (!instanceData || !instanceData[0]) {
        console.log("instanceData not ready, waiting...");
        toast.info("Loading instance data...", { position: "top-center" });
        return;
      }

      const userProfile = await getData(getThemeURL);

      if (
        !userProfile ||
        !Array.isArray(userProfile) ||
        userProfile.length === 0
      ) {
        toast.error("Failed to fetch user profile", { position: "top-center" });
        return;
      }

      // Rest of your fetchApiData function remains the same...
      const user = userProfile[0];
      const roleName = user.role_name;

      const instance = instanceData[0];
      const fullInstanceUrl = instance.instance_url + selectedApiObj.url;

      let inputobj = {
        instanceUrl: fullInstanceUrl,
        apiType: selectedApiObj.apiType,
      };

      if (roleName === "SUPER ADMIN") {
        inputobj.type = "DEFAULT";
      } else if (user.is_admin === true) {
        inputobj.type = "HO";
        inputobj.bank_id = user.bank_id;
      } else {
        inputobj.type = "U";
        inputobj.bank_id = user.bank_id;
      }

      if (selectedApiObj.apiType === "POST" && selectedApiObj.request) {
        try {
          const requestData = JSON.parse(selectedApiObj.request);
          Object.keys(requestData).forEach((key) => {
            inputobj[key] = requestData[key];
          });
        } catch (parseError) {
          console.error("Error parsing request field:", parseError);
        }
      }

      const response = await fetch("/nifi/external-api", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(inputobj),
      });

      const data = await response.json();

      if (isJoinApi) {
        if (data && data.data && Array.isArray(data.data)) {
          const columns = data.data.map((col) => col.columnName);
          const types = {};
          data.data.forEach((col) => {
            types[col.columnName] = col.dataType || "string";
          });

          setJoinApiColumns((prev) => ({
            ...prev,
            [targetApiId]: {
              columns,
              dataTypes: types,
            },
          }));

          const tableName = extractTableNameFromUrl(selectedApiObj.url);
          toast.success(`Join API data loaded for ${tableName}`, {
            position: "top-center",
          });
        }
      } else {
        if (data && data.data && Array.isArray(data.data)) {
          const columns = data.data.map((col) => col.columnName);
          const types = {};
          data.data.forEach((col) => {
            types[col.columnName] = col.dataType || "string";
          });

          setApiColumns(columns);
          setColumnDataTypes(types);

          // Simplified success message without auto-selection
          if (editId) {
            toast.success("API data refreshed for edit mode", {
              position: "top-center",
            });
          } else {
            toast.success("API data fetched successfully", {
              position: "top-center",
            });
          }
        }
      }
    } catch (error) {
      console.error("Error fetching API data:", error);
      if (!isJoinApi) {
      }
      toast.error("Failed to fetch API data", { position: "top-center" });
    } finally {
      setLoadingApi(false);
      if (isJoinApi) {
        // setLoadingColumns(false);
      }
    }
  };

  // Enhanced Join functionality (adapted from DynamicDashboard)
  const addJoin = () => {
    if (!selectedApi) {
      toast.error("Please select a main API first", {
        position: "top-center",
      });
      return;
    }

    const joinErrors = validateJoins();
    if (joinErrors.length > 0) {
      toast.error(
        `Please fix existing join issues before adding a new join: ${joinErrors[0]}`,
        { position: "top-center" }
      );
      return;
    }

    if (joins.length >= 20) {
      toast.warning("Maximum of 20 joins allowed for performance reasons", {
        position: "top-center",
      });
      return;
    }

    const newJoin = {
      api: "",
      type: "INNER",
      conditions: [
        {
          leftApi: selectedApi,
          firstApiColumn: "",
          secondApiColumn: "",
          operator: "=",
          conditionType: "AND",
        },
      ],
    };

    setJoins([...joins, newJoin]);
    toast.success("New join added successfully", { position: "top-center" });
  };

  const updateJoin = (index, field, value) => {
    if (field === "api" && value) {
      const existingApis = joins
        .map((join, idx) => (idx !== index ? join.api : null))
        .filter(Boolean);

      if (existingApis.includes(value)) {
        toast.error(`API is already being used in another join`, {
          position: "top-center",
        });
        return;
      }

      if (value === selectedApi) {
        toast.error("Cannot join an API with itself", {
          position: "top-center",
        });
        return;
      }
    }

    const newJoins = [...joins];

    // Clear columns and conditions when API changes (from DynamicDashboard logic)
    if (field === "api" && value !== newJoins[index].api) {
      newJoins[index] = {
        ...newJoins[index],
        [field]: value,
        columns: [],
        conditions: [
          {
            leftApi: selectedApi,
            firstApiColumn: "",
            secondApiColumn: "",
            operator: "=",
            conditionType: "AND",
          },
        ],
      };

      toast.info(
        `Table changed to ${getTableNameByApiId(
          value
        )}. Previous column selections and join conditions have been reset.`,
        {
          position: "top-center",
          autoClose: 4000,
        }
      );
    } else {
      newJoins[index] = { ...newJoins[index], [field]: value };
    }

    if (field === "api" && value) {
      if (!joinApiColumns[value]) {
        fetchColumnsForJoinApi(value);
        const tableName = getTableNameByApiId(value);
        toast.info(`Loading columns for table: ${tableName}`, {
          position: "top-center",
        });
      }
    }

    setJoins(newJoins);
  };

  const removeJoin = (index) => {
    const joinToRemove = joins[index];
    const tableName = getTableNameByApiId(joinToRemove.api);

    if (
      joinToRemove.api &&
      joinToRemove.conditions.some((c) => c.firstApiColumn && c.secondApiColumn)
    ) {
      // Show custom confirmation modal for configured joins
      setConfirmModal({
        isOpen: true,
        title: "Remove Join ?",
        message: `Are you sure you want to remove the join with '${tableName}'? This will also remove any selected columns from this table.`,
        onConfirm: () => {
          performRemoveJoin(index);
          setConfirmModal({
            isOpen: false,
            title: "",
            message: "",
            onConfirm: null,
          });
        },
      });
    } else {
      // Directly remove if no configuration exists
      performRemoveJoin(index);
    }
  };

  // Extract the actual removal logic into a separate function
  const performRemoveJoin = (index) => {
    const newJoins = [...joins];
    const removedJoin = newJoins.splice(index, 1)[0];
    const tableName = getTableNameByApiId(removedJoin.api);

    const apiStillUsed = newJoins.some((j) => j.api === removedJoin.api);
    if (!apiStillUsed && removedJoin.api) {
      setJoinApiColumns((prev) => {
        const newState = { ...prev };
        delete newState[removedJoin.api];
        return newState;
      });
    }

    setJoins(newJoins);
    toast.success(`Join with '${tableName}' removed`, {
      position: "top-center",
    });
  };

  const addJoinCondition = (joinIndex) => {
    const join = joins[joinIndex];

    if (!join.api) {
      toast.error("Please select a join API before adding conditions", {
        position: "top-center",
      });
      return;
    }

    if (join.conditions.length >= 20) {
      toast.warning(
        "Maximum of 20 conditions per join recommended for performance",
        { position: "top-center" }
      );
      return;
    }

    const newJoins = [...joins];
    newJoins[joinIndex].conditions.push({
      leftApi: selectedApi,
      firstApiColumn: "",
      secondApiColumn: "",
      operator: "=",
      conditionType: "AND",
    });
    setJoins(newJoins);
    toast.success("Join condition added", { position: "top-center" });
  };

  const updateJoinCondition = (joinIndex, conditionIndex, field, value) => {
    const newJoins = [...joins];
    const currentCondition = newJoins[joinIndex].conditions[conditionIndex];

    // Validate column selection based on API (existing logic)
    if (field === "firstApiColumn" && value) {
      const leftApi = currentCondition.leftApi || selectedApi;
      const availableColumns =
        leftApi === selectedApi
          ? apiColumns
          : joinApiColumns[leftApi]?.columns || [];

      if (!availableColumns.includes(value)) {
        toast.error(
          `Column '${value}' not found in ${getTableNameByApiId(leftApi)}`,
          {
            position: "top-center",
          }
        );
        return;
      }
    }

    if (field === "secondApiColumn" && value) {
      const joinApi = newJoins[joinIndex].api;
      const joinColumns = joinApiColumns[joinApi]?.columns || [];
      if (!joinColumns.includes(value)) {
        toast.error(`Column '${value}' not found in join API`, {
          position: "top-center",
        });
        return;
      }
    }

    // Handle operator reset when columns change
    if (field === "firstApiColumn" || field === "secondApiColumn") {
      // Get data types for both columns after the change
      const leftApiId = currentCondition.leftApi || selectedApi;
      const joinApi = newJoins[joinIndex].api;

      let leftColumnName =
        field === "firstApiColumn" ? value : currentCondition.firstApiColumn;
      let rightColumnName =
        field === "secondApiColumn" ? value : currentCondition.secondApiColumn;

      // Get data types
      const leftColumnDataType = leftColumnName
        ? leftApiId === selectedApi
          ? columnDataTypes[leftColumnName]
          : joinApiColumns[leftApiId]?.dataTypes?.[leftColumnName]
        : "";

      const rightColumnDataType = rightColumnName
        ? joinApiColumns[joinApi]?.dataTypes?.[rightColumnName]
        : "";

      // Use the more restrictive data type for operators (prioritize the selected columns)
      const relevantDataType =
        leftColumnName && rightColumnName
          ? leftColumnDataType || rightColumnDataType // Use whichever is available
          : leftColumnDataType || rightColumnDataType || "";

      // Get available operators for the data type
      const availableOperators = getOperatorsByDataType(relevantDataType);

      // Check if current operator is valid for the data type
      const currentOperator = currentCondition.operator;
      const isCurrentOperatorValid =
        availableOperators.includes(currentOperator);

      // Update the condition with field change and operator adjustment
      newJoins[joinIndex].conditions[conditionIndex] = {
        ...currentCondition,
        [field]: value,
        // Reset operator if current one is not valid for the new data type
        operator: isCurrentOperatorValid
          ? currentOperator
          : availableOperators[0] || "=",
      };
    } else if (field === "leftApi") {
      // When left API changes, reset both column and operator
      newJoins[joinIndex].conditions[conditionIndex] = {
        ...currentCondition,
        leftApi: value,
        firstApiColumn: "",
        operator: "=", // Reset to default
      };
    } else {
      // For other fields (operator, conditionType), update normally
      newJoins[joinIndex].conditions[conditionIndex] = {
        ...currentCondition,
        [field]: value,
      };
    }

    setJoins(newJoins);
  };

  const removeJoinCondition = (joinIndex, conditionIndex) => {
    const newJoins = [...joins];
    newJoins[joinIndex].conditions.splice(conditionIndex, 1);
    setJoins(newJoins);
  };

  const toggleJoinColumnSelection = (joinIndex, column) => {
    const newJoins = [...joins];
    const joinColumns = newJoins[joinIndex].columns || [];

    if (joinColumns.includes(column)) {
      newJoins[joinIndex].columns = joinColumns.filter((col) => col !== column);
    } else {
      newJoins[joinIndex].columns = [...joinColumns, column];
    }

    setJoins(newJoins);
  };

  // Enhanced validation functions (from DynamicDashboard)
  const validateJoins = () => {
    const errors = [];

    const joinApis = joins.map((join) => join.api).filter((api) => api);
    const duplicates = joinApis.filter(
      (api, index) => joinApis.indexOf(api) !== index
    );

    if (duplicates.length > 0) {
      const duplicateNames = [...new Set(duplicates)].map((apiId) =>
        getTableNameByApiId(apiId)
      );
      errors.push(
        `Duplicate joins detected for tables: ${duplicateNames.join(", ")}`
      );
    }

    const selfJoins = joins.filter((join) => join.api === selectedApi);
    if (selfJoins.length > 0) {
      errors.push("Cannot join a table with itself");
    }

    joins.forEach((join, index) => {
      const tableName = getTableNameByApiId(join.api);

      if (!join.api) {
        errors.push(`Join ${index + 1}: Please select an API to join`);
      }

      if (join.conditions.length === 0) {
        errors.push(
          `Join ${
            index + 1
          } (${tableName}): At least one join condition is required`
        );
      }

      join.conditions.forEach((condition, condIndex) => {
        if (!condition.firstApiColumn) {
          const mainTableName = getTableNameByApiId(selectedApi);
          errors.push(
            `Join ${index + 1} condition ${
              condIndex + 1
            }: Please select a column from ${mainTableName}`
          );
        }
        if (!condition.secondApiColumn) {
          errors.push(
            `Join ${index + 1} condition ${
              condIndex + 1
            }: Please select a column from ${tableName}`
          );
        }
      });
    });

    return errors;
  };

  const validateJoinDataTypes = () => {
    const errors = [];

    joins.forEach((join, joinIndex) => {
      if (!join.api) return;

      join.conditions.forEach((condition, condIndex) => {
        const { firstApiColumn, secondApiColumn } = condition;

        if (firstApiColumn && secondApiColumn) {
          const leftApi = condition.leftApi || selectedApi;
          const firstColumnType =
            leftApi === selectedApi
              ? columnDataTypes[firstApiColumn]
              : joinApiColumns[leftApi]?.dataTypes?.[firstApiColumn];
          const secondColumnType =
            joinApiColumns[join.api]?.dataTypes?.[secondApiColumn];

          if (firstColumnType && secondColumnType) {
            const isCompatible = areTypesCompatible(
              firstColumnType,
              secondColumnType
            );

            if (!isCompatible) {
              errors.push(
                `Join ${joinIndex + 1} condition ${condIndex + 1}: ` +
                  `Column types don't match - ${getTableNameByApiId(
                    leftApi
                  )}.${firstApiColumn} (${firstColumnType}) ` +
                  `and ${getTableNameByApiId(
                    join.api
                  )}.${secondApiColumn} (${secondColumnType})`
              );
            }
          }
        }
      });
    });

    return errors;
  };

  const areTypesCompatible = (type1, type2) => {
    const normalizeType = (type) => {
      const lower = type.toLowerCase();
      if (
        lower.includes("int") ||
        lower.includes("bigint") ||
        lower.includes("smallint")
      )
        return "integer";
      if (
        lower.includes("varchar") ||
        lower.includes("text") ||
        lower.includes("char")
      )
        return "string";
      if (
        lower.includes("decimal") ||
        lower.includes("numeric") ||
        lower.includes("float") ||
        lower.includes("double")
      )
        return "decimal";
      if (lower.includes("date") || lower.includes("timestamp")) return "date";
      if (lower.includes("boolean") || lower.includes("bool")) return "boolean";
      return lower;
    };

    return normalizeType(type1) === normalizeType(type2);
  };

  // Enhanced validation functions (from DynamicDashboard)
  const validateValue = (field, value, operator, apiId = null) => {
    if (!field || !value) return { isValid: true, error: null };

    let dataType;
    if (apiId && apiId !== selectedApi) {
      dataType = joinApiColumns[apiId]?.dataTypes?.[field];
    } else {
      dataType = columnDataTypes[field];
    }

    if (!dataType) return { isValid: true, error: null };

    if (["IN", "NOT IN"].includes(operator)) {
      const values = value.split(",").map((v) => v.trim());
      if (values.length === 0) {
        return { isValid: false, error: "At least one value is required" };
      }

      for (let i = 0; i < values.length; i++) {
        const validation = validateSingleValue(dataType, values[i]);
        if (!validation.isValid) {
          return {
            isValid: false,
            error: `Value ${i + 1}: ${validation.error}`,
          };
        }
      }
      return { isValid: true, error: null };
    }

    if (operator === "BETWEEN") {
      const values = value.split(",").map((v) => v.trim());
      if (values.length !== 2) {
        return {
          isValid: false,
          error: "BETWEEN requires exactly 2 comma-separated values",
        };
      }

      for (let i = 0; i < values.length; i++) {
        const validation = validateSingleValue(dataType, values[i]);
        if (!validation.isValid) {
          return {
            isValid: false,
            error: `Value ${i + 1}: ${validation.error}`,
          };
        }
      }

      // Additional validation for BETWEEN - ensure first value is less than second for numeric/date types
      const lowerDataType = dataType.toLowerCase();
      if (
        lowerDataType.includes("int") ||
        lowerDataType.includes("numeric") ||
        lowerDataType.includes("decimal") ||
        lowerDataType.includes("float")
      ) {
        const num1 = Number(values[0]);
        const num2 = Number(values[1]);
        if (num1 >= num2) {
          return {
            isValid: false,
            error: "First value must be less than second value",
          };
        }
      } else if (lowerDataType.includes("date")) {
        const date1 = new Date(values[0]);
        const date2 = new Date(values[1]);
        if (date1 >= date2) {
          return {
            isValid: false,
            error: "Start date must be before end date",
          };
        }
      }

      return { isValid: true, error: null };
    }

    return validateSingleValue(dataType, value);
  };

  const validateSingleValue = (dataType, value) => {
    if (!dataType || !value) return { isValid: true, error: null };

    try {
      const lowerDataType = dataType.toLowerCase();

      // Integer validation
      if (lowerDataType.includes("int") || lowerDataType === "bigint") {
        const isValid =
          !isNaN(value) &&
          Number.isInteger(Number(value)) &&
          value.toString().indexOf(".") === -1;
        return {
          isValid,
          error: isValid ? null : `Must be a whole number (e.g., 123, -456)`,
        };
      }

      // Decimal/Float validation
      if (
        lowerDataType.includes("numeric") ||
        lowerDataType.includes("decimal") ||
        lowerDataType.includes("float") ||
        lowerDataType.includes("double")
      ) {
        const isValid = !isNaN(value) && isFinite(Number(value));
        return {
          isValid,
          error: isValid
            ? null
            : `Must be a valid number (e.g., 123.45, -67.89)`,
        };
      }

      // Boolean validation
      if (lowerDataType.includes("boolean")) {
        const validBooleans = ["true", "false", "1", "0"];
        const isValid = validBooleans.includes(value.toLowerCase());
        return {
          isValid,
          error: isValid ? null : `Must be true, false, 1, or 0`,
        };
      }

      // Date validation
      if (
        lowerDataType.includes("date") ||
        lowerDataType.includes("timestamp")
      ) {
        const dateValue = new Date(value);
        const isValid =
          !isNaN(dateValue.getTime()) &&
          value.match(/^\d{4}-\d{2}-\d{2}/) !== null;
        return {
          isValid,
          error: isValid
            ? null
            : `Must be a valid date (YYYY-MM-DD or YYYY-MM-DD HH:MM:SS)`,
        };
      }

      // String validation (varchar, text, char)
      if (
        lowerDataType.includes("varchar") ||
        lowerDataType.includes("text") ||
        lowerDataType.includes("char")
      ) {
        // For string types, most values are valid, but we can check for reasonable length
        const isValid = value.length <= 5000; // reasonable string length limit
        return {
          isValid,
          error: isValid ? null : `Text too long (max 5000 characters)`,
        };
      }

      return { isValid: true, error: null };
    } catch (e) {
      return { isValid: false, error: "Invalid input format" };
    }
  };

  // Enhanced query validation (from DynamicDashboard)
  const isValidQuery = useMemo(() => {
    if (!selectedApi || !selectedWidgetCategory) return false;

    const validConditions = conditions.every((cond) => {
      if (!cond.field || !cond.value) return true;
      return validateValue(cond.field, cond.value, cond.operator, cond.api);
    });

    const validOrderBy = orderByFields.every((field) => field.column);

    const validJoins = joins.every((join) => {
      if (!join.api) return false;
      return join.conditions.every(
        (cond) => cond.firstApiColumn && cond.secondApiColumn
      );
    });

    return validConditions && validOrderBy && validJoins;
  }, [
    selectedApi,
    selectedWidgetCategory,
    conditions,
    orderByFields,
    columnDataTypes,
    joins,
    joinApiColumns,
  ]);

  // Enhanced helper functions (adapted from DynamicDashboard)
  const addOrderByField = () => {
    setOrderByFields([
      ...orderByFields,
      {
        api: selectedApi,
        column: "",
        direction: "ASC",
      },
    ]);
  };

  const addAggregator = () => {
    setAggregators([
      ...aggregators,
      {
        api: selectedApi,
        function: "SUM",
        column: "",
        alias: "",
      },
    ]);
  };

  const removeAggregator = (index) => {
    const newAggregators = [...aggregators];
    newAggregators.splice(index, 1);
    setAggregators(newAggregators);
  };

  const updateAggregator = (index, field, value) => {
    const newAggregators = [...aggregators];

    if (field === "api") {
      newAggregators[index] = {
        ...newAggregators[index],
        [field]: value,
        column: "",
      };
    } else {
      newAggregators[index] = { ...newAggregators[index], [field]: value };
    }

    setAggregators(newAggregators);
  };

  const addGroupByField = () => {
    setGroupByFields([
      ...groupByFields,
      {
        api: selectedApi,
        column: "",
      },
    ]);
  };

  // Add this useEffect after the existing useEffects in your ApiBuilder component
  console.log("QUERY TO EDIT", queryToEdit);
  // Replace the existing edit useEffect with this one
  useEffect(() => {
    if (
      queryToEdit &&
      allUrls.length > 0 &&
      instanceData &&
      instanceData.length > 0
    ) {
      console.log("Starting edit mode setup...");

      const {
        serviceName: editServiceName,
        serviceDescription: editServiceDescription,
        queryBuilder,
        id,
        widgetType,
      } = queryToEdit;

      // Set basic fields first
      setServiceName(editServiceName || "");
      setServiceDescription(editServiceDescription || "");
      setSelectedWidgetCategory(widgetType || "");

      // Extract the main table name from entity
      const mainTableName = queryBuilder.entity?.split(".")[1] || "";
      console.log("Main table name:", mainTableName);

      // Find the corresponding API based on the table name
      const correspondingApi = allUrls.find((api) => {
        const apiTableName = extractTableNameFromUrl(api.url);
        return apiTableName === mainTableName;
      });

      console.log("Found corresponding API:", correspondingApi);

      if (correspondingApi) {
        // Set the selected API first
        setSelectedApi(String(correspondingApi.id));

        // Process columns to separate regular columns and aggregators
        const regularColumns = [];
        const processedAggregators = [];

        (queryBuilder.columns || []).forEach((col) => {
          console.log("Processing column:", col);

          // Check if column contains aggregator function
          const aggregatorMatch = col.match(
            /^(SUM|MIN|MAX|AVG|COUNT)\(([^)]+)\)$/
          );

          if (aggregatorMatch) {
            const [, func, column] = aggregatorMatch;
            console.log("Found aggregator:", func, column);

            // Extract table and column from the column reference
            let tableName, columnName;
            if (column.includes(".")) {
              [tableName, columnName] = column.split(".");
            } else {
              // If no table prefix, assume it's the main table
              tableName = mainTableName;
              columnName = column;
            }

            // Find the API ID for this table
            const apiForTable = allUrls.find((api) => {
              const apiTableName = extractTableNameFromUrl(api.url);
              return apiTableName === tableName;
            });

            processedAggregators.push({
              function: func,
              api: apiForTable
                ? String(apiForTable.id)
                : String(correspondingApi.id),
              column: columnName,
              alias: "",
            });
          } else if (col.startsWith(`${mainTableName}.`)) {
            const columnName = col.split(".")[1];
            regularColumns.push(columnName);
            console.log("Added regular column:", columnName);
          }
        });

        // Set columns and aggregators
        setSelectedColumns(regularColumns);
        setAggregators(processedAggregators);

        console.log("Regular columns:", regularColumns);
        console.log("Processed aggregators:", processedAggregators);

        // Set the JSON queries
        setCompactJsonQuery(JSON.stringify(queryBuilder));
        setJsonQuery(JSON.stringify(queryBuilder, null, 2));

        // Process conditions/filters
        const processedConditions = [];
        if (queryBuilder.filters) {
          for (const field in queryBuilder.filters) {
            const val = queryBuilder.filters[field];

            // Extract table and column name from the field
            let tableName, columnName;
            if (field.includes(".")) {
              [tableName, columnName] = field.split(".");
            } else {
              tableName = mainTableName;
              columnName = field;
            }

            // Find the API ID for this table
            const apiForTable = allUrls.find((api) => {
              const apiTableName = extractTableNameFromUrl(api.url);
              return apiTableName === tableName;
            });

            const apiId = apiForTable
              ? String(apiForTable.id)
              : String(correspondingApi.id);

            if (typeof val === "object" && val.operator) {
              let processedValue;

              if (typeof val.value === "boolean") {
                processedValue = val.value.toString(); // Convert boolean to string
              } else if (["IN", "NOT IN"].includes(val.operator)) {
                processedValue = Array.isArray(val.value)
                  ? val.value.join(",")
                  : val.value;
              } else if (val.operator === "BETWEEN") {
                processedValue = Array.isArray(val.value)
                  ? val.value.join(",")
                  : val.value;
              } else {
                processedValue = Array.isArray(val.value)
                  ? val.value.join(",")
                  : val.value;
              }

              processedConditions.push({
                api: apiId,
                field: columnName,
                operator: val.operator,
                value: processedValue,
              });
            } else {
              let processedValue = val;
              if (typeof val === "boolean") {
                processedValue = val.toString(); // Convert boolean to string
              }
              processedConditions.push({
                api: apiId,
                field: columnName,
                operator: "=",
                value: processedValue,
              });
            }
          }
        }
        setConditions(processedConditions);

        // Process order by fields
        if (Array.isArray(queryBuilder.orderBy)) {
          const processedOrderBy = queryBuilder.orderBy.map((field) => {
            if (field.column.includes(".")) {
              const [tableName, columnName] = field.column.split(".");

              const apiForTable = allUrls.find((api) => {
                const apiTableName = extractTableNameFromUrl(api.url);
                return apiTableName === tableName;
              });

              return {
                api: apiForTable
                  ? String(apiForTable.id)
                  : String(correspondingApi.id),
                column: columnName,
                direction: field.direction,
              };
            } else {
              return {
                api: String(correspondingApi.id),
                column: field.column,
                direction: field.direction,
              };
            }
          });
          setOrderByFields(processedOrderBy);
        }

        // Process group by fields
        if (Array.isArray(queryBuilder.groupBy)) {
          const processedGroupBy = queryBuilder.groupBy.map((field) => {
            if (field.includes(".")) {
              const [tableName, columnName] = field.split(".");

              const apiForTable = allUrls.find((api) => {
                const apiTableName = extractTableNameFromUrl(api.url);
                return apiTableName === tableName;
              });

              return {
                api: apiForTable
                  ? String(apiForTable.id)
                  : String(correspondingApi.id),
                column: columnName,
              };
            } else {
              return {
                api: String(correspondingApi.id),
                column: field,
              };
            }
          });
          setGroupByFields(processedGroupBy);
        }

        // Process joins
        if (Array.isArray(queryBuilder.joins)) {
          const processedJoins = queryBuilder.joins.map((join) => {
            const joinTableName = join.table.split(".")[1];
            const alias = join.alias || joinTableName;

            const joinApi = allUrls.find((api) => {
              const apiTableName = extractTableNameFromUrl(api.url);
              return apiTableName === joinTableName;
            });

            const joinColumns = (queryBuilder.columns || [])
              .filter((col) => col.startsWith(`${alias}.`))
              .map((col) => col.split(".")[1]);

            const conditions = [];

            if (join.on) {
              const conditionParts = join.on.split(/( and | or )/i);
              let currentConditionType = "AND";

              for (let i = 0; i < conditionParts.length; i++) {
                const part = conditionParts[i].trim();
                if (
                  part.toLowerCase() === "and" ||
                  part.toLowerCase() === "or"
                ) {
                  currentConditionType = part.toUpperCase();
                  continue;
                }

                if (part) {
                  const [left, right] = part.split(/\s*=\s*/);

                  let leftTable, leftColumn;
                  if (left.includes(".")) {
                    [leftTable, leftColumn] = left.split(".");
                  } else {
                    leftTable = mainTableName;
                    leftColumn = left;
                  }

                  const leftApi = allUrls.find((api) => {
                    const apiTableName = extractTableNameFromUrl(api.url);
                    return apiTableName === leftTable;
                  });

                  let rightColumn;
                  if (right.includes(".")) {
                    [, rightColumn] = right.split(".");
                  } else {
                    rightColumn = right;
                  }

                  conditions.push({
                    leftApi: leftApi
                      ? String(leftApi.id)
                      : String(correspondingApi.id),
                    firstApiColumn: leftColumn,
                    secondApiColumn: rightColumn,
                    operator: "=",
                    conditionType: currentConditionType,
                  });
                }
              }
            }

            return {
              api: joinApi ? String(joinApi.id) : "",
              type: join.type,
              conditions,
              columns: joinColumns,
            };
          });

          setJoins(processedJoins);
        }

        // Auto-open query builder when editing
        setShowQueryBuilder(true);
        setIsQueryBuilderExpanded(true);
        setEditId(id);

        // Now fetch the API data after everything is set up
        // Use setTimeout to ensure state updates are processed
        setTimeout(async () => {
          console.log("Fetching API data for:", correspondingApi.id);
          try {
            await fetchApiData(correspondingApi.id, false);
            await fetchApiUrlFromGetAllUrls(correspondingApi.id);

            // Fetch columns for join APIs if any
            if (queryBuilder.joins && Array.isArray(queryBuilder.joins)) {
              for (const join of queryBuilder.joins) {
                const joinTableName = join.table.split(".")[1];
                const joinApi = allUrls.find((api) => {
                  const apiTableName = extractTableNameFromUrl(api.url);
                  return apiTableName === joinTableName;
                });

                if (joinApi) {
                  await fetchColumnsForJoinApi(String(joinApi.id));
                }
              }
            }

            toast.success("Edit data loaded successfully", {
              position: "top-center",
            });
          } catch (error) {
            console.error("Error loading edit data:", error);
            toast.error("Error loading some edit data", {
              position: "top-center",
            });
          }
        }, 500); // Increased delay to ensure all state updates are processed
      } else {
        toast.error("Could not find corresponding API for this query", {
          position: "top-center",
        });
      }
    }
  }, [queryToEdit, allUrls, instanceData]); // Keep the same dependencies

  // Enhanced buildJsonQuery (comprehensive logic from DynamicDashboard)
  const buildJsonQuery = () => {
    if (!selectedApi) {
      toast.error("Please select an API", { position: "top-center" });
      return;
    }

    if (!selectedWidgetCategory) {
      toast.error("Please select a Widget Category", {
        position: "top-center",
      });
      return;
    }

    // Validate joins first
    const joinErrors = validateJoins();
    if (joinErrors.length > 0) {
      toast.error(`Join validation failed: ${joinErrors[0]}`, {
        position: "top-center",
      });
      return;
    }

    // Validate join data types
    const typeErrors = validateJoinDataTypes();
    if (typeErrors.length > 0) {
      toast.warning(`Data type mismatch: ${typeErrors[0]}`, {
        position: "top-center",
      });
    }

    if (!isValidQuery) {
      toast.error("Some filter values, joins, or order fields are invalid", {
        position: "top-center",
      });
      return;
    }

    // Show success message for valid joins
    if (joins.length > 0 && !typeErrors.length > 0) {
      toast.success(`Query built successfully with ${joins.length} join(s)`, {
        position: "top-center",
      });
    }

    const selectedApiObj = allUrls.find(
      (api) => String(api.id) === selectedApi
    );

    const mainTableName = extractTableNameFromUrl(selectedApiObj?.url);

    // Start with base query object (same format as DynamicDashboard)
    const queryObject = {
      entity: `core.${mainTableName}`,
      columns: [],
      filters: {},
    };

    // Add aggregator columns (same logic as DynamicDashboard)
    if (aggregators.length > 0) {
      aggregators.forEach((agg) => {
        if (agg.function && agg.column && agg.api) {
          const tableName = getTableNameByApiId(agg.api);
          const aggregatorColumn = `${agg.function}(${tableName}.${agg.column})`;
          queryObject.columns.push(aggregatorColumn);
        }
      });
    }

    // Add join columns (same logic as DynamicDashboard)
    joins.forEach((join) => {
      if (join.columns && join.columns.length > 0 && join.api) {
        const tableName = getTableNameByApiId(join.api);
        const availableColumns = joinApiColumns[join.api]?.columns || [];

        join.columns.forEach((col) => {
          if (availableColumns.includes(col)) {
            queryObject.columns.push(`${tableName}.${col}`);
          } else {
            console.warn(
              `Column '${col}' not found in API '${join.api}', skipping...`
            );
          }
        });
      }
    });

    // Add selected columns from main API (same logic as DynamicDashboard)
    queryObject.columns.push(
      ...selectedColumns.map((col) => `${mainTableName}.${col}`)
    );

    // Process filters with support for multiple conditions on same field
    if (conditions.length > 0) {
      // Group conditions by field
      const groupedConditions = {};

      conditions.forEach((condition) => {
        if (condition.api && condition.field && condition.value) {
          const tableName = getTableNameByApiId(condition.api);
          const dataTypes =
            condition.api === selectedApi
              ? columnDataTypes
              : joinApiColumns[condition.api]?.dataTypes || {};

          const dataType = dataTypes[condition.field]?.toLowerCase() || "";
          const prefixedField = `${tableName}.${condition.field}`;

          // Initialize array for this field if it doesn't exist
          if (!groupedConditions[prefixedField]) {
            groupedConditions[prefixedField] = [];
          }

          let processedCondition;

          // Handle created_on field specially for BETWEEN operator
          if (
            condition.field.toLowerCase() === "created_on" &&
            condition.operator === "BETWEEN"
          ) {
            const values = condition.value.split(",").map((v) => v.trim());

            if (values.length === 2) {
              const startDate = values[0];
              const endDate = values[1];

              let formattedStartDate = startDate;
              if (!startDate.includes(":")) {
                formattedStartDate = `${startDate} 00:00:00.000`;
              }

              let formattedEndDate = endDate;
              if (!endDate.includes(":")) {
                formattedEndDate = `${endDate} 23:59:59.999`;
              }

              processedCondition = {
                operator: "BETWEEN",
                value: [formattedStartDate, formattedEndDate],
              };
            }
          } else if (["IN", "NOT IN"].includes(condition.operator)) {
            const values = condition.value.split(",").map((v) => {
              const trimmed = v.trim();
              if (dataType.includes("int") || dataType.includes("numeric")) {
                return isNaN(trimmed) ? trimmed : Number(trimmed);
              }
              return trimmed;
            });
            processedCondition = {
              operator: condition.operator,
              value: values,
            };
          } else if (condition.operator === "BETWEEN") {
            const values = condition.value.split(",").map((v) => {
              const trimmed = v.trim();
              if (dataType.includes("int") || dataType.includes("numeric")) {
                return isNaN(trimmed) ? trimmed : Number(trimmed);
              }
              return trimmed;
            });

            if (values.length === 2) {
              processedCondition = {
                operator: "BETWEEN",
                value: values,
              };
            }
          } else {
            let processedValue = condition.value;
            if (dataType.includes("int") || dataType.includes("numeric")) {
              processedValue = isNaN(condition.value)
                ? condition.value
                : Number(condition.value);
            } else if (dataType.includes("boolean")) {
              processedValue =
                condition.value.toLowerCase() === "true" ||
                condition.value === "1";
            }

            if (["LIKE", "NOT LIKE"].includes(condition.operator)) {
              processedValue = condition.value.includes("%")
                ? condition.value
                : `%${condition.value}%`;
              processedCondition = {
                operator: condition.operator,
                value: processedValue,
              };
            } else if (
              ["=", "!=", ">", "<", ">=", "<="].includes(condition.operator)
            ) {
              processedCondition = {
                operator: condition.operator,
                value: processedValue,
              };
            } else {
              // For simple equality, store just the value
              processedCondition = processedValue;
            }
          }

          if (processedCondition !== undefined) {
            groupedConditions[prefixedField].push(processedCondition);
          }
        }
      });

      // Process grouped conditions and add to filters
      Object.keys(groupedConditions).forEach((field) => {
        const conditionsForField = groupedConditions[field];

        if (conditionsForField.length === 1) {
          // Single condition - use existing format
          queryObject.filters[field] = conditionsForField[0];
        } else {
          // Multiple conditions - use array format
          queryObject.filters[field] = conditionsForField;
        }
      });
    }

    // Process joins (exact same format as DynamicDashboard)
    if (joins.length > 0) {
      queryObject.joins = joins
        .filter((join) => join.api && join.conditions.length > 0)
        .map((join) => {
          const tableName = getTableNameByApiId(join.api);

          let onCondition = "";
          join.conditions.forEach((cond, idx) => {
            if (idx > 0) {
              onCondition += ` ${cond.conditionType} `;
            }

            const leftApiId = cond.leftApi || selectedApi;
            const leftTableName = getTableNameByApiId(leftApiId);
            const leftSide = `${leftTableName}.${cond.firstApiColumn}`;
            const rightSide = `${tableName}.${cond.secondApiColumn}`;

            onCondition += `${leftSide} ${cond.operator} ${rightSide}`;
          });

          return {
            table: `core.${tableName}`,
            type: join.type,
            on: onCondition,
            alias: tableName,
          };
        });
    }

    // Process order by (same format as DynamicDashboard)
    if (orderByFields.length > 0) {
      queryObject.orderBy = orderByFields
        .filter((field) => field.api && field.column)
        .map((field) => {
          const tableName = getTableNameByApiId(field.api);
          return {
            column: `${tableName}.${field.column}`,
            direction: field.direction,
          };
        });
    }

    // Process GROUP BY (same format as DynamicDashboard)
    if (groupByFields.length > 0) {
      queryObject.groupBy = groupByFields
        .filter((field) => field.api && field.column)
        .map((field) => {
          const tableName = getTableNameByApiId(field.api);
          return `${tableName}.${field.column}`;
        });
    }

    setJsonQuery(JSON.stringify(queryObject, null, 2));
    setCompactJsonQuery(JSON.stringify(queryObject));
  };

  const callNiFiAPI = async () => {
    try {
      setIsExecuting(true);

      // Parse the existing JSON query for the main object
      const queryObject = JSON.parse(jsonQuery);

      // Fetch user profile to determine auth parameters
      const userProfile = await getData(getThemeURL);

      if (
        !userProfile ||
        !Array.isArray(userProfile) ||
        userProfile.length === 0
      ) {
        toast.error("Failed to fetch user profile", { position: "top-center" });
        return;
      }

      const user = userProfile[0];
      const roleName = user.role_name;
      const userBankId = user.bank_id;

      // Get instance data
      if (!instanceData || !instanceData[0]) {
        toast.error("Instance data not available", { position: "top-center" });
        return;
      }

      // Prepare auth object based on user role with updated structure
      const authObject = {
        instanceUrl: "/query-builder-api/execute", // Remove base URL part
        apiType: "POST",
      };

      if (roleName === "SUPER ADMIN") {
        authObject.type = "SA"; // Changed from "DEFAULT" to "SA"
        // Get bankid from instanceData instead of user profile
        const instanceBankId = instanceData[0].bank_id;
        authObject.bank_id = instanceBankId ? [instanceBankId] : []; // Make it array
      } else if (user.is_admin === true) {
        authObject.type = "HO";
        authObject.bank_id = userBankId ? [userBankId] : []; // Make it array
      } else {
        authObject.type = "U";
        authObject.bank_id = userBankId ? [userBankId] : []; // Make it array
      }

      // Clone the query object to avoid modifying the original
      const executionQueryObject = { ...queryObject };

      // Prepare the final payload
      const payload = {
        auth: authObject,
        main: executionQueryObject,
      };

      // Call the API endpoint
      const response = await fetch("/nifi/fetchQuery-API", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.status}`);
      }

      const data = await response.json();
      setJsonResponse(data);
      // setShowJsonResponse(true);
      toast.success("Query executed successfully", { position: "top-center" });
    } catch (error) {
      console.error("Error executing query:", error);
      toast.error(`Failed to fetch response: ${error.message}`, {
        position: "top-center",
      });
    } finally {
      setIsExecuting(false);
    }
  };

  // Save Query functionality
  const saveQuery = async () => {
    if (!selectedWidgetCategory) {
      toast.error("Please select a Widget Category", {
        position: "top-center",
      });
      return;
    }

    try {
      setIsExecuting(true);

      // Fetch user ID and role before saving
      let fetchedUserId = null;
      let roleType = "";
      let userRoleFromAPI = null;
      let userBankId = null;

      try {
        const response = await getData(getThemeURL);
        console.log(response, "User profile response");
        if (response && Array.isArray(response) && response.length > 0) {
          fetchedUserId = response[0].id;
          const roleName = response[0].role_name?.toUpperCase();
          userRoleFromAPI = roleName;
          userBankId = response[0].bank_id;
          console.log("Role name:", roleName);
          console.log("userBankId:", userBankId);

          if (roleName === "SUPER ADMIN") {
            roleType = "SA";
          } else if (response[0].is_admin === true) {
            roleType = "HO";
          } else {
            roleType = "U";
          }
          console.log("Mapped role type:", roleType);
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
        toast.error("Failed to fetch user profile.", {
          position: "top-center",
        });
        return;
      }

      if (!fetchedUserId) {
        toast.error("User ID is not available. Cannot save query.", {
          position: "top-center",
        });
        return;
      }

      // Get instance data to construct the correct apiEndPointUrl
      if (!instanceData || !instanceData[0]) {
        toast.error("Instance data not available", { position: "top-center" });
        return;
      }

      const instance = instanceData[0];
      const apiEndPointUrl =
        instance.instance_url + "/query-builder-api/execute";

      // Ensure we're saving the original query without any static filters
      let queryToSave = compactJsonQuery;

      console.log("QUERY BUILDER PAYLOAD", queryToSave);

      const payload = {
        serviceName: serviceName,
        serviceDescription: serviceDescription,
        queryBuilder: queryToSave, // Use cleaned query
        widgetType: selectedWidgetCategory,
        created_by: fetchedUserId,
        roleType: roleType,
        active_status: true,
        duplicateNameAllow: false,
        queryStage: "PRIMARY",
        apiEndPointUrl: apiEndPointUrl,
      };

      // Add bankId if user has one (important for role-based access)
      if (userBankId) {
        payload.bankId = userBankId;
      }

      if (editId) {
        payload.id = editId;
      }

      const response = await fetch("/nifi/saveServicebuilderAPI", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed to save query: ${response.status}`);
      }
      console.log(response, "nifiresponse");

      const data = await response.json();
      console.log("nifidata", data);

      // Handle response based on status
      if (data.status === "success") {
        toast.success(data.message || "Query saved successfully!", {
          position: "top-center",
        });
        navigate("../save-api-query-list");
      } else if (data.status === "error") {
        // Handle specific error cases
        if (data.code === 409) {
          toast.error(
            data.message ||
              "Service name already exists. Please choose a different name.",
            {
              position: "top-center",
            }
          );
        } else {
          toast.error(data.message || "Failed to save query.", {
            position: "top-center",
          });
        }
        // Don't navigate on error
      } else {
        // Fallback for unexpected response format
        toast.error("Unexpected response format.", {
          position: "top-center",
        });
      }
    } catch (error) {
      toast.error(`Failed to save query: ${error.message}`, {
        position: "top-center",
      });
    } finally {
      setIsExecuting(false);
    }
  };

  // Helper functions for conditions, columns, etc. (enhanced from DynamicDashboard)
  const addCondition = () => {
    setConditions([
      ...conditions,
      {
        api: selectedApi,
        field: "",
        operator: "=",
        value: "",
      },
    ]);
  };

  const removeCondition = (index) => {
    const newConditions = [...conditions];
    newConditions.splice(index, 1);
    setConditions(newConditions);
  };

  const updateCondition = (index, field, value) => {
    const newConditions = [...conditions];

    if (field === "api") {
      // If API is being changed, reset the field and operator
      newConditions[index] = {
        ...newConditions[index],
        [field]: value,
        field: "",
        operator: "=", // Reset to default operator
      };
    } else if (field === "field") {
      // If field (column) is being changed, check if current operator is valid for new data type
      const currentCondition = newConditions[index];
      const apiId = currentCondition.api;

      // Get data type for the new field
      let dataType;
      if (apiId && apiId !== selectedApi) {
        dataType = joinApiColumns[apiId]?.dataTypes?.[value];
      } else {
        dataType = columnDataTypes[value];
      }

      // Get available operators for the new data type
      const availableOperators = getOperatorsByDataType(dataType);

      // Check if current operator is valid for new data type
      const currentOperator = currentCondition.operator;
      const isCurrentOperatorValid =
        availableOperators.includes(currentOperator);

      // Update the condition
      newConditions[index] = {
        ...newConditions[index],
        [field]: value,
        // Reset operator to first available one if current operator is not valid for new data type
        operator: isCurrentOperatorValid
          ? currentOperator
          : availableOperators[0] || "=",
      };
    } else {
      // For other fields (like operator, value), just update normally
      newConditions[index] = { ...newConditions[index], [field]: value };
    }

    setConditions(newConditions);
  };

  const toggleColumnSelection = (column) => {
    setSelectedColumns((prev) =>
      prev.includes(column)
        ? prev.filter((col) => col !== column)
        : [...prev, column]
    );
  };

  const selectAllColumns = () => {
    const availableColumns = getAllAvailableColumns();
    if (availableColumns.length > 0) {
      setSelectedColumns([...availableColumns]);
    }
  };

  const clearAllColumns = () => {
    setSelectedColumns([]);
  };

  // Update this helper function to work better in edit mode
  const getAllAvailableColumns = () => {
    // If we have loaded API columns, use them
    if (apiColumns.length > 0) {
      return apiColumns;
    }

    // In edit mode, if API data isn't loaded yet, show existing selected columns
    if (editId && selectedColumns.length > 0) {
      return selectedColumns;
    }

    return [];
  };

  // Update this helper function to work better in edit mode
  const getColumnsForDropdowns = (apiId) => {
    if (!apiId) return [];

    if (apiId === selectedApi) {
      // In edit mode, if API columns aren't loaded yet, try to get columns from existing selections
      if (apiColumns.length > 0) {
        return apiColumns;
      } else if (editId && selectedColumns.length > 0) {
        // Return existing selected columns as options when API data isn't loaded yet
        return selectedColumns;
      } else {
        return [];
      }
    }

    // For join APIs
    if (joinApiColumns[apiId]?.columns) {
      return joinApiColumns[apiId].columns;
    } else if (editId) {
      // In edit mode, check if we have columns from join configuration
      const joinWithColumns = joins.find(
        (j) => j.api === apiId && j.columns?.length > 0
      );
      return joinWithColumns?.columns || [];
    }

    return [];
  };

  // Add this helper function to get available APIs for dropdowns
  const getAvailableApisForDropdowns = () => {
    const availableApis = [];

    // Always include the main API if selected
    if (selectedApi) {
      availableApis.push({
        id: selectedApi,
        name: getTableNameByApiId(selectedApi),
        isMain: true,
      });
    }

    // Add join APIs that have columns loaded OR are configured in edit mode
    joins.forEach((join) => {
      if (join.api && !availableApis.find((api) => api.id === join.api)) {
        availableApis.push({
          id: join.api,
          name: getTableNameByApiId(join.api),
          isMain: false,
        });
      }
    });

    return availableApis;
  };

  return (
    <div
      style={{
        minHeight: "60vh",
        backgroundColor: "#f9fafb",
        padding: "1rem",
      }}
    >
      <div
        style={{ maxWidth: "80rem", marginLeft: "auto", marginRight: "auto" }}
      >
        {/* Header */}
        <div
          style={{
            borderRadius: "0.75rem",
            padding: "2rem 1.5rem",
            marginBottom: "-1.5rem",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "1.25rem",
              position: "relative",
            }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "0.75rem",
                alignItems: "center",
              }}
            >
              <button
                onClick={() => setShowQueryBuilder(!showQueryBuilder)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  padding: "0.625rem 1.25rem",
                  borderRadius: "0.5rem",
                  border: "none",
                  cursor: "pointer",
                  backgroundColor: "#1a7a73",
                  color: "white",
                  fontWeight: "500",
                  marginLeft: "-23px",
                }}
              >
                <Sliders size={18} style={{ flexShrink: 0 }} />
                <span>API Query Builder</span>
              </button>
              <button
                onClick={saveQuery}
                disabled={
                  isExecuting ||
                  !selectedApi ||
                  !selectedWidgetCategory ||
                  (jsonResponse && jsonResponse.status === "error")
                }
                style={{
                  padding: "0.5rem 1rem",
                  backgroundColor:
                    isExecuting ||
                    !selectedApi ||
                    !selectedWidgetCategory ||
                    (jsonResponse && jsonResponse.status === "error")
                      ? "#9ca3af"
                      : "#1a7a73",
                  color: "white",
                  borderRadius: "0.5rem",
                  border: "none",
                  cursor:
                    isExecuting ||
                    !selectedApi ||
                    !selectedWidgetCategory ||
                    (jsonResponse && jsonResponse.status === "error")
                      ? "not-allowed"
                      : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                {isExecuting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    {editId ? "Updating..." : "Saving..."}
                  </>
                ) : (
                  <>
                    <CheckSquare size={16} />
                    {editId ? "Update Query" : "Save Query"}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Query Builder */}
        {showQueryBuilder && (
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "0.75rem",
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
              marginBottom: "1.5rem",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "1rem",
                cursor: "pointer",
                backgroundColor: "#f9fafb",
              }}
              onClick={() => setIsQueryBuilderExpanded(!isQueryBuilderExpanded)}
            >
              <h2
                style={{
                  fontSize: "1.25rem",
                  fontWeight: "600",
                  color: "#1f2937",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <Code style={{ color: "#1a7a73" }} size={20} />
                API Query Builder
              </h2>
              <button
                style={{
                  color: "#6b7280",
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                }}
              >
                {isQueryBuilderExpanded ? (
                  <ChevronUp size={20} />
                ) : (
                  <ChevronDown size={20} />
                )}
              </button>
            </div>

            {isQueryBuilderExpanded && (
              <div
                style={{ padding: "1.5rem", borderTop: "1px solid #e5e7eb" }}
              >
                {/* Basic Settings */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "1rem",
                    marginBottom: "1.5rem",
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "0.5rem",
                        fontWeight: "500",
                      }}
                    >
                      Service Name <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Service Name"
                      value={serviceName}
                      onChange={(e) => setServiceName(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "0.5rem 0.75rem",
                        border: "1px solid #d1d5db",
                        borderRadius: "0.375rem",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "0.5rem",
                        fontWeight: "500",
                      }}
                    >
                      Select API <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <select
                      value={selectedApi}
                      onChange={handleApiChange}
                      style={{
                        width: "100%",
                        padding: "0.5rem 0.75rem",
                        border: "1px solid #d1d5db",
                        borderRadius: "0.375rem",
                      }}
                    >
                      <option value="">Select an API</option>
                      {Array.isArray(allUrls) &&
                        allUrls.map((api) => (
                          <option key={api.id} value={api.id}>
                            {api.endPointName}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "0.5rem",
                        fontWeight: "500",
                      }}
                    >
                      Service Description
                    </label>
                    <input
                      type="text"
                      placeholder="Service Description"
                      value={serviceDescription}
                      onChange={(e) => setServiceDescription(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "0.5rem 0.75rem",
                        border: "1px solid #d1d5db",
                        borderRadius: "0.375rem",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "0.5rem",
                        fontWeight: "500",
                      }}
                    >
                      Select Widget Category{" "}
                      <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <select
                      value={selectedWidgetCategory}
                      onChange={(e) =>
                        setSelectedWidgetCategory(e.target.value)
                      }
                      style={{
                        width: "100%",
                        padding: "0.5rem 0.75rem",
                        border: "1px solid #d1d5db",
                        borderRadius: "0.375rem",
                      }}
                    >
                      <option value="">Select a widget type</option>
                      {Widgets.map((widget) => (
                        <option key={widget.value} value={widget.value}>
                          {widget.display}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Column Selection */}
                {((selectedApi && (apiColumns.length > 0 || loadingApi)) ||
                  (editId && selectedColumns.length > 0)) && (
                  <div
                    style={{
                      backgroundColor: "#f0f9ff",
                      borderRadius: "0.5rem",
                      padding: "1rem",
                      marginBottom: "1.5rem",
                      border: "1px solid #1a7a73",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "1rem",
                      }}
                    >
                      <h3
                        style={{
                          fontSize: "0.875rem",
                          fontWeight: "600",
                          color: "#0f3d3a",
                        }}
                      >
                        Select Columns
                        {loadingApi && (
                          <span
                            style={{
                              fontSize: "0.75rem",
                              color: "#4b5563",
                              marginLeft: "0.5rem",
                              fontWeight: "normal",
                            }}
                          >
                            <Loader2
                              size={12}
                              className="animate-spin inline"
                              style={{ marginRight: "0.25rem" }}
                            />
                            Loading columns...
                          </span>
                        )}
                        {editId && !apiColumns.length && !loadingApi && (
                          <span
                            style={{
                              fontSize: "0.75rem",
                              color: "#4b5563",
                              marginLeft: "0.5rem",
                              fontWeight: "normal",
                            }}
                          >
                            (Previously selected columns shown - columns will
                            load automatically)
                          </span>
                        )}
                      </h3>
                      <div style={{ display: "flex", gap: "0.5rem" }}>
                        <button
                          onClick={selectAllColumns}
                          disabled={
                            getAllAvailableColumns().length === 0 || loadingApi
                          }
                          style={{
                            padding: "0.25rem 0.5rem",
                            backgroundColor: "#1a7a73",
                            color: "white",
                            borderRadius: "0.25rem",
                            border: "none",
                            cursor: "pointer",
                            opacity:
                              getAllAvailableColumns().length === 0 ||
                              loadingApi
                                ? 0.5
                                : 1,
                          }}
                        >
                          Select All
                        </button>
                        <button
                          onClick={clearAllColumns}
                          disabled={loadingApi}
                          style={{
                            padding: "0.25rem 0.5rem",
                            backgroundColor: "#64748b",
                            color: "white",
                            borderRadius: "0.25rem",
                            border: "none",
                            cursor: "pointer",
                            opacity: loadingApi ? 0.5 : 1,
                          }}
                        >
                          Clear All
                        </button>
                      </div>
                    </div>

                    {loadingApi ? (
                      <div
                        style={{
                          padding: "2rem",
                          textAlign: "center",
                          color: "#6b7280",
                          fontSize: "0.875rem",
                        }}
                      >
                        <Loader2
                          size={20}
                          className="animate-spin"
                          style={{ marginBottom: "0.5rem" }}
                        />
                        <div>Loading available columns...</div>
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fill, minmax(200px, 1fr))",
                          gap: "0.5rem",
                          maxHeight: "200px",
                          overflowY: "auto",
                          padding: "0.5rem",
                          border: "1px solid #e5e7eb",
                          borderRadius: "0.375rem",
                          backgroundColor: "white",
                        }}
                      >
                        {getAllAvailableColumns().map((column) => (
                          <label
                            key={column}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                              cursor: "pointer",
                              padding: "0.25rem",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedColumns.includes(column)}
                              onChange={() => toggleColumnSelection(column)}
                            />
                            <span>{column}</span>
                          </label>
                        ))}
                      </div>
                    )}

                    {editId &&
                      !apiColumns.length &&
                      !loadingApi &&
                      selectedColumns.length > 0 && (
                        <div
                          style={{
                            marginTop: "0.5rem",
                            padding: "0.5rem",
                            backgroundColor: "#fef3c7",
                            borderRadius: "0.25rem",
                            fontSize: "0.875rem",
                            color: "#92400e",
                          }}
                        >
                          💡 Columns will load automatically when you select an
                          API above.
                        </div>
                      )}
                  </div>
                )}

                {/* Joins Section - Enhanced from DynamicDashboard */}
                {(selectedApi || editId) && (
                  <div style={{ marginBottom: "1.5rem" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "0.5rem",
                      }}
                    >
                      <h3 style={{ fontSize: "0.875rem", fontWeight: "600" }}>
                        Table Joins
                        <span
                          style={{
                            fontSize: "0.75rem",
                            color: "#4b5563",
                            marginLeft: "0.5rem",
                            fontWeight: "normal",
                          }}
                        >
                          (Main Table: {getTableNameByApiId(selectedApi)})
                        </span>
                      </h3>
                    </div>

                    {joins.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "1rem",
                          marginBottom: "1rem",
                        }}
                      >
                        {joins.map((join, joinIndex) => (
                          <div
                            key={joinIndex}
                            style={{
                              border: "1px solid #e5e7eb",
                              borderRadius: "0.5rem",
                              padding: "1rem",
                              backgroundColor: "#f8fafc",
                              position: "relative",
                            }}
                          >
                            <div
                              style={{
                                position: "absolute",
                                top: "0.5rem",
                                right: "0.5rem",
                                display: "flex",
                                gap: "0.5rem",
                              }}
                            >
                              <button
                                onClick={() => removeJoin(joinIndex)}
                                style={{
                                  padding: "0.25rem",
                                  color: "#ef4444",
                                  backgroundColor: "transparent",
                                  border: "none",
                                  cursor: "pointer",
                                }}
                                title="Remove join"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>

                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: "1rem",
                                marginBottom: "1rem",
                              }}
                            >
                              {/* Join Type */}
                              <div>
                                <label
                                  style={{
                                    display: "block",
                                    marginBottom: "0.25rem",
                                    fontSize: "0.75rem",
                                    color: "#64748b",
                                  }}
                                >
                                  Join Type
                                </label>
                                <select
                                  value={join.type}
                                  onChange={(e) =>
                                    updateJoin(
                                      joinIndex,
                                      "type",
                                      e.target.value
                                    )
                                  }
                                  style={{
                                    width: "100%",
                                    padding: "0.5rem",
                                    borderRadius: "0.25rem",
                                    border: "1px solid #d1d5db",
                                  }}
                                >
                                  {joinTypes.map((type) => (
                                    <option key={type} value={type}>
                                      {type} JOIN
                                    </option>
                                  ))}
                                </select>
                              </div>

                              {/* Join Table */}
                              <div>
                                <label
                                  style={{
                                    display: "block",
                                    marginBottom: "0.25rem",
                                    fontSize: "0.75rem",
                                    color: "#64748b",
                                  }}
                                >
                                  Join Table
                                </label>
                                <select
                                  value={join.api}
                                  onChange={(e) =>
                                    updateJoin(joinIndex, "api", e.target.value)
                                  }
                                  style={{
                                    width: "100%",
                                    padding: "0.5rem",
                                    borderRadius: "0.25rem",
                                    border: "1px solid #d1d5db",
                                  }}
                                >
                                  <option value="">Select table to join</option>
                                  {allUrls
                                    .filter(
                                      (api) => String(api.id) !== selectedApi
                                    )
                                    .map((api) => (
                                      <option key={api.id} value={api.id}>
                                        {getTableNameByApiId(api.id)}
                                      </option>
                                    ))}
                                </select>
                              </div>
                            </div>

                            {/* Auto-generated ON condition preview */}
                            {join.api && join.conditions.length > 0 && (
                              <div style={{ marginBottom: "1rem" }}>
                                <label
                                  style={{
                                    display: "block",
                                    marginBottom: "0.25rem",
                                    fontSize: "0.75rem",
                                    color: "#64748b",
                                  }}
                                >
                                  ON Condition
                                </label>
                                <div
                                  style={{
                                    padding: "0.75rem",
                                    backgroundColor: "#f3f4f6",
                                    borderRadius: "0.375rem",
                                    border: "1px solid #e5e7eb",
                                    fontSize: "0.875rem",
                                    color: "#374151",
                                    fontFamily: "monospace",
                                  }}
                                >
                                  {join.conditions.map((cond, i) => {
                                    const leftApiId =
                                      cond.leftApi || selectedApi;
                                    const leftTableName =
                                      getTableNameByApiId(leftApiId);
                                    const joinTableName = getTableNameByApiId(
                                      join.api
                                    );

                                    return (
                                      <div key={i}>
                                        {i > 0 && (
                                          <span style={{ color: "#6b7280" }}>
                                            {cond.conditionType || "AND"}{" "}
                                          </span>
                                        )}
                                        <span>
                                          {leftTableName}.
                                          {cond.firstApiColumn || ""}{" "}
                                          {cond.operator || "="} {joinTableName}
                                          .{cond.secondApiColumn || ""}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            {/* Join Conditions - Enhanced with table dropdown */}
                            <div style={{ marginBottom: "1rem" }}>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  marginBottom: "0.5rem",
                                }}
                              >
                                <h4
                                  style={{
                                    fontSize: "0.75rem",
                                    fontWeight: "600",
                                    color: "#4b5563",
                                  }}
                                >
                                  Join Conditions
                                </h4>
                                <button
                                  onClick={() => addJoinCondition(joinIndex)}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.25rem",
                                    padding: "0.25rem 0.5rem",
                                    backgroundColor: "#10b981",
                                    color: "white",
                                    borderRadius: "0.25rem",
                                    border: "none",
                                    cursor: "pointer",
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  <Plus size={12} />
                                  Add Condition
                                </button>
                              </div>

                              {join.conditions.map((condition, condIndex) => {
                                // Get available APIs for left side (main + previously added joins)
                                const availableLeftApis = [
                                  {
                                    id: selectedApi,
                                    name: getTableNameByApiId(selectedApi),
                                    isMain: true,
                                  },
                                  ...joins
                                    .filter(
                                      (j, idx) =>
                                        idx < joinIndex &&
                                        j.api &&
                                        joinApiColumns[j.api]?.columns
                                    )
                                    .map((j) => ({
                                      id: j.api,
                                      name: getTableNameByApiId(j.api),
                                      isMain: false,
                                    })),
                                ];

                                // Get columns for left side (based on selected API)
                                const leftApiId =
                                  condition.leftApi || selectedApi;
                                const leftColumns =
                                  leftApiId === selectedApi
                                    ? apiColumns
                                    : joinApiColumns[leftApiId]?.columns || [];

                                // Get columns for right side (always from current join API)
                                const rightColumns =
                                  joinApiColumns[join.api]?.columns || [];

                                // Get data types for both columns
                                const leftColumnDataType =
                                  condition.firstApiColumn
                                    ? leftApiId === selectedApi
                                      ? columnDataTypes[
                                          condition.firstApiColumn
                                        ]
                                      : joinApiColumns[leftApiId]?.dataTypes?.[
                                          condition.firstApiColumn
                                        ]
                                    : "";

                                const rightColumnDataType =
                                  condition.secondApiColumn
                                    ? joinApiColumns[join.api]?.dataTypes?.[
                                        condition.secondApiColumn
                                      ]
                                    : "";

                                // Use the more restrictive data type for operators (prioritize the selected columns)
                                const relevantDataType =
                                  condition.firstApiColumn &&
                                  condition.secondApiColumn
                                    ? leftColumnDataType || rightColumnDataType // Use whichever is available
                                    : leftColumnDataType ||
                                      rightColumnDataType ||
                                      "";

                                const availableOperators =
                                  getOperatorsByDataType(relevantDataType);

                                return (
                                  <div
                                    key={condIndex}
                                    style={{
                                      display: "flex",
                                      gap: "0.5rem",
                                      alignItems: "center",
                                      marginBottom: "0.5rem",
                                      flexWrap: "wrap",
                                      position: "relative", // Add this for positioning warnings
                                    }}
                                  >
                                    {condIndex > 0 && (
                                      <select
                                        value={condition.conditionType || "AND"}
                                        onChange={(e) =>
                                          updateJoinCondition(
                                            joinIndex,
                                            condIndex,
                                            "conditionType",
                                            e.target.value
                                          )
                                        }
                                        style={{
                                          width: "80px",
                                          padding: "0.5rem",
                                          borderRadius: "0.25rem",
                                          border: "1px solid #d1d5db",
                                        }}
                                      >
                                        {conditionTypes.map((type) => (
                                          <option key={type} value={type}>
                                            {type}
                                          </option>
                                        ))}
                                      </select>
                                    )}

                                    {/* Left Table Dropdown */}
                                    <select
                                      value={condition.leftApi || selectedApi}
                                      onChange={(e) => {
                                        const newApi = e.target.value;
                                        updateJoinCondition(
                                          joinIndex,
                                          condIndex,
                                          "leftApi",
                                          newApi
                                        );
                                        updateJoinCondition(
                                          joinIndex,
                                          condIndex,
                                          "firstApiColumn",
                                          ""
                                        );
                                      }}
                                      style={{
                                        width: "150px",
                                        padding: "0.5rem",
                                        borderRadius: "0.25rem",
                                        border: "1px solid #d1d5db",
                                      }}
                                    >
                                      {availableLeftApis.map((api) => (
                                        <option
                                          key={`left-${api.id}`}
                                          value={api.id}
                                        >
                                          {api.name}
                                        </option>
                                      ))}
                                    </select>

                                    {/* Left Column Dropdown */}
                                    <select
                                      value={condition.firstApiColumn || ""}
                                      onChange={(e) =>
                                        updateJoinCondition(
                                          joinIndex,
                                          condIndex,
                                          "firstApiColumn",
                                          e.target.value
                                        )
                                      }
                                      style={{
                                        flex: 1,
                                        minWidth: "150px",
                                        padding: "0.5rem",
                                        borderRadius: "0.25rem",
                                        border: "1px solid #d1d5db",
                                      }}
                                    >
                                      <option value="">
                                        Select {getTableNameByApiId(leftApiId)}{" "}
                                        column
                                      </option>
                                      {leftColumns.map((column) => (
                                        <option key={column} value={column}>
                                          {column}
                                        </option>
                                      ))}
                                    </select>

                                    {/* LEFT COLUMN DATA TYPE DISPLAY BOX */}
                                    <input
                                      type="text"
                                      value={leftColumnDataType || ""}
                                      placeholder="Data Type"
                                      disabled
                                      style={{
                                        width: "100px",
                                        padding: "0.5rem",
                                        borderRadius: "0.25rem",
                                        border: "1px solid #d1d5db",
                                        backgroundColor: "#f3f4f6",
                                        color: "#6b7280",
                                      }}
                                    />

                                    {/* Operator - NOW DYNAMIC BASED ON DATA TYPE */}
                                    <select
                                      value={condition.operator || "="}
                                      onChange={(e) =>
                                        updateJoinCondition(
                                          joinIndex,
                                          condIndex,
                                          "operator",
                                          e.target.value
                                        )
                                      }
                                      style={{
                                        width: "80px",
                                        padding: "0.5rem",
                                        borderRadius: "0.25rem",
                                        border: "1px solid #d1d5db",
                                      }}
                                      title={`Available operators for ${
                                        relevantDataType || "selected"
                                      } data type`}
                                    >
                                      {availableOperators.map((op) => (
                                        <option key={op} value={op}>
                                          {op}
                                        </option>
                                      ))}
                                    </select>

                                    {/* Right Table Dropdown (fixed to current join table) */}
                                    <select
                                      value={join.api}
                                      disabled
                                      style={{
                                        width: "150px",
                                        padding: "0.5rem",
                                        borderRadius: "0.25rem",
                                        border: "1px solid #d1d5db",
                                        backgroundColor: "#f3f4f6",
                                        color: "#6b7280",
                                      }}
                                    >
                                      <option value={join.api}>
                                        {getTableNameByApiId(join.api)}
                                      </option>
                                    </select>

                                    {/* Right Column Dropdown */}
                                    <select
                                      value={condition.secondApiColumn || ""}
                                      onChange={(e) =>
                                        updateJoinCondition(
                                          joinIndex,
                                          condIndex,
                                          "secondApiColumn",
                                          e.target.value
                                        )
                                      }
                                      disabled={!join.api}
                                      style={{
                                        flex: 1,
                                        minWidth: "150px",
                                        padding: "0.5rem",
                                        borderRadius: "0.25rem",
                                        border: "1px solid #d1d5db",
                                        opacity: join.api ? 1 : 0.7,
                                      }}
                                    >
                                      <option value="">
                                        Select {getTableNameByApiId(join.api)}{" "}
                                        column
                                      </option>
                                      {rightColumns.map((column) => (
                                        <option key={column} value={column}>
                                          {column}
                                        </option>
                                      ))}
                                    </select>

                                    {/* RIGHT COLUMN DATA TYPE DISPLAY BOX */}
                                    <input
                                      type="text"
                                      value={rightColumnDataType || ""}
                                      placeholder="Data Type"
                                      disabled
                                      style={{
                                        width: "100px",
                                        padding: "0.5rem",
                                        borderRadius: "0.25rem",
                                        border: "1px solid #d1d5db",
                                        backgroundColor: "#f3f4f6",
                                        color: "#6b7280",
                                      }}
                                    />

                                    {/* Remove Condition Button */}
                                    <button
                                      onClick={() =>
                                        removeJoinCondition(
                                          joinIndex,
                                          condIndex
                                        )
                                      }
                                      style={{
                                        padding: "0.5rem",
                                        color: "#ef4444",
                                        backgroundColor: "transparent",
                                        border: "none",
                                        cursor: "pointer",
                                      }}
                                    >
                                      <Trash2 size={16} />
                                    </button>

                                    {/* Data type compatibility warning */}
                                    {condition.firstApiColumn &&
                                      condition.secondApiColumn &&
                                      leftColumnDataType &&
                                      rightColumnDataType &&
                                      !areTypesCompatible(
                                        leftColumnDataType,
                                        rightColumnDataType
                                      ) && (
                                        <div
                                          style={{
                                            position: "absolute",
                                            bottom: "-22px",
                                            left: "0",
                                            fontSize: "0.75rem",
                                            color: "#ef4444",
                                            backgroundColor: "#fef2f2",
                                            padding: "2px 6px",
                                            borderRadius: "4px",
                                            border: "1px solid #fecaca",
                                            whiteSpace: "nowrap",
                                            zIndex: 10,
                                          }}
                                        >
                                          ⚠️ Type mismatch: {leftColumnDataType}{" "}
                                          vs {rightColumnDataType}
                                        </div>
                                      )}
                                  </div>
                                );
                              })}
                            </div>

                            {/* Join Column Selection */}
                            {join.api && (
                              <div
                                style={{
                                  marginTop: "1rem",
                                  backgroundColor: "#f0f9ff",
                                  borderRadius: "0.5rem",
                                  padding: "0.75rem",
                                  border: "1px solid #1a7a73",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: "0.5rem",
                                  }}
                                >
                                  <h4
                                    style={{
                                      fontSize: "0.75rem",
                                      fontWeight: "600",
                                      color: "#0f3d3a",
                                    }}
                                  >
                                    Select columns from{" "}
                                    {getTableNameByApiId(join.api)}
                                  </h4>
                                  <div
                                    style={{ display: "flex", gap: "0.5rem" }}
                                  >
                                    <button
                                      onClick={() => {
                                        const allColumns =
                                          joinApiColumns[join.api]?.columns ||
                                          [];
                                        updateJoin(
                                          joinIndex,
                                          "columns",
                                          allColumns
                                        );
                                      }}
                                      style={{
                                        padding: "0.25rem 0.5rem",
                                        fontSize: "0.625rem",
                                        backgroundColor: "#1a7a73",
                                        color: "white",
                                        borderRadius: "0.25rem",
                                        border: "none",
                                        cursor: "pointer",
                                      }}
                                    >
                                      Select All
                                    </button>
                                    <button
                                      onClick={() =>
                                        updateJoin(joinIndex, "columns", [])
                                      }
                                      style={{
                                        padding: "0.25rem 0.5rem",
                                        fontSize: "0.625rem",
                                        backgroundColor: "#64748b",
                                        color: "white",
                                        borderRadius: "0.25rem",
                                        border: "none",
                                        cursor: "pointer",
                                      }}
                                    >
                                      Clear All
                                    </button>
                                  </div>
                                </div>

                                {joinApiColumns[join.api]?.columns?.length >
                                0 ? (
                                  <div
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns:
                                        "repeat(auto-fill, minmax(180px, 1fr))",
                                      gap: "0.5rem",
                                      maxHeight: "150px",
                                      overflowY: "auto",
                                      padding: "0.5rem",
                                      border: "1px solid #e5e7eb",
                                      borderRadius: "0.375rem",
                                      backgroundColor: "white",
                                    }}
                                  >
                                    {joinApiColumns[join.api].columns.map(
                                      (column) => {
                                        return (
                                          <label
                                            key={column}
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "0.5rem",
                                              cursor: "pointer",
                                              padding: "0.25rem",
                                              whiteSpace: "nowrap",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                            }}
                                          >
                                            <input
                                              type="checkbox"
                                              checked={(
                                                join.columns || []
                                              ).includes(column)}
                                              onChange={() =>
                                                toggleJoinColumnSelection(
                                                  joinIndex,
                                                  column
                                                )
                                              }
                                            />
                                            <span
                                              style={{ fontSize: "0.75rem" }}
                                            >
                                              {column}
                                            </span>
                                          </label>
                                        );
                                      }
                                    )}
                                  </div>
                                ) : (
                                  <div
                                    style={{
                                      padding: "0.5rem",
                                      textAlign: "center",
                                      color: "#64748b",
                                      fontSize: "0.75rem",
                                    }}
                                  >
                                    {joinApiColumns[join.api]
                                      ? "No columns available"
                                      : "Loading columns..."}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add Join button */}
                    <button
                      onClick={addJoin}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.25rem",
                        padding: "0.5rem 1rem",
                        backgroundColor: "#8b5cf6",
                        color: "white",
                        borderRadius: "0.5rem",
                        border: "none",
                        cursor: "pointer",
                        marginLeft: "auto",
                      }}
                    >
                      <Plus size={16} />
                      Add Join
                    </button>
                  </div>
                )}

                {/* Filters Section - Enhanced with table dropdown */}
                {(selectedApi || editId) && (
                  <div style={{ marginBottom: "1.5rem" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "0.5rem",
                      }}
                    >
                      <h3 style={{ fontSize: "0.875rem", fontWeight: "600" }}>
                        Filters
                      </h3>
                      <button
                        onClick={addCondition}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.25rem",
                          padding: "0.25rem 0.5rem",
                          backgroundColor: "#1a7a73",
                          color: "white",
                          borderRadius: "0.25rem",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        <Plus size={14} />
                        Add Filter
                      </button>
                    </div>

                    {conditions.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.5rem",
                        }}
                      >
                        {conditions.map((condition, index) => {
                          // Get available APIs (main + joins) - Enhanced from DynamicDashboard
                          const availableApis = getAvailableApisForDropdowns();

                          // Get current columns based on selected API
                          const currentColumns =
                            condition.api === selectedApi
                              ? apiColumns
                              : joinApiColumns[condition.api]?.columns || [];

                          // Get data type for validation AND operator filtering
                          const dataType =
                            condition.field && condition.api
                              ? condition.api === selectedApi
                                ? columnDataTypes[condition.field]
                                : joinApiColumns[condition.api]?.dataTypes?.[
                                    condition.field
                                  ]
                              : "";

                          // Get available operators based on data type
                          const availableOperators =
                            getOperatorsByDataType(dataType);

                          // Enhanced validation with detailed error messages
                          const validation =
                            condition.field && condition.value
                              ? validateValue(
                                  condition.field,
                                  condition.value,
                                  condition.operator,
                                  condition.api
                                )
                              : { isValid: true, error: null };

                          // Enhanced placeholder logic from DynamicDashboard
                          const getPlaceholder = () => {
                            if (condition.operator === "BETWEEN") {
                              if (
                                dataType?.toLowerCase().includes("date") ||
                                dataType?.toLowerCase().includes("timestamp")
                              ) {
                                return "2024-01-01, 2024-12-31";
                              } else if (
                                dataType?.toLowerCase().includes("int")
                              ) {
                                return "1, 100";
                              } else if (
                                dataType?.toLowerCase().includes("numeric") ||
                                dataType?.toLowerCase().includes("decimal")
                              ) {
                                return "0.0, 999.99";
                              }
                              return "Start Value, End Value";
                            }
                            if (["IN", "NOT IN"].includes(condition.operator)) {
                              if (dataType?.toLowerCase().includes("int")) {
                                return "1, 2, 3, 4";
                              } else if (
                                dataType?.toLowerCase().includes("varchar") ||
                                dataType?.toLowerCase().includes("text")
                              ) {
                                return "Value1, Value2, Value3";
                              }
                              return "Comma separated values";
                            }
                            if (
                              dataType?.toLowerCase().includes("date") ||
                              dataType?.toLowerCase().includes("timestamp")
                            ) {
                              return "YYYY-MM-DD or YYYY-MM-DD HH:MM:SS";
                            } else if (
                              dataType?.toLowerCase().includes("int")
                            ) {
                              return "Enter whole number (e.g., 123)";
                            } else if (
                              dataType?.toLowerCase().includes("numeric") ||
                              dataType?.toLowerCase().includes("decimal")
                            ) {
                              return "Enter decimal number (e.g., 123.45)";
                            } else if (
                              dataType?.toLowerCase().includes("boolean")
                            ) {
                              return "true, false, 1, or 0";
                            } else if (
                              dataType?.toLowerCase().includes("varchar") ||
                              dataType?.toLowerCase().includes("text")
                            ) {
                              return "Enter text value";
                            }
                            return "Enter value";
                          };

                          return (
                            <div
                              key={index}
                              style={{
                                display: "flex",
                                gap: "0.5rem",
                                alignItems: "flex-start",
                                position: "relative",
                                marginTop: "10px",
                                marginBottom: validation.error
                                  ? "25px"
                                  : "10px", // Extra space for error message
                              }}
                            >
                              {/* Table Selection Dropdown */}
                              <select
                                value={condition.api || ""}
                                onChange={(e) => {
                                  const newApi = e.target.value;
                                  updateCondition(index, "api", newApi);
                                }}
                                style={{
                                  width: "200px",
                                  padding: "0.5rem",
                                  borderRadius: "0.25rem",
                                  border: "1px solid #d1d5db",
                                }}
                              >
                                <option value="">Select Table</option>
                                {availableApis.map((api) => (
                                  <option
                                    key={`filter-api-${api.id}`}
                                    value={api.id}
                                  >
                                    {api.name}
                                  </option>
                                ))}
                              </select>

                              {/* Field Selection Dropdown */}
                              <select
                                value={condition.field}
                                onChange={(e) =>
                                  updateCondition(
                                    index,
                                    "field",
                                    e.target.value
                                  )
                                }
                                disabled={!condition.api}
                                style={{
                                  flex: 1,
                                  padding: "0.5rem",
                                  borderRadius: "0.25rem",
                                  border: "1px solid #d1d5db",
                                  opacity: condition.api ? 1 : 0.7,
                                }}
                              >
                                <option value="">Select field</option>
                                {getColumnsForDropdowns(condition.api).map(
                                  (field) => (
                                    <option key={field} value={field}>
                                      {field}
                                    </option>
                                  )
                                )}
                              </select>

                              {/* Data Type Display */}
                              <input
                                type="text"
                                value={dataType || ""}
                                placeholder="Data Type"
                                disabled
                                style={{
                                  width: "100px",
                                  padding: "0.5rem",
                                  borderRadius: "0.25rem",
                                  border: "1px solid #d1d5db",
                                  backgroundColor: "#f3f4f6",
                                  color: "#6b7280",
                                }}
                              />

                              {/* Operator Selection - NOW DYNAMIC BASED ON DATA TYPE */}
                              <select
                                value={condition.operator}
                                onChange={(e) =>
                                  updateCondition(
                                    index,
                                    "operator",
                                    e.target.value
                                  )
                                }
                                style={{
                                  width: "120px",
                                  padding: "0.5rem",
                                  borderRadius: "0.25rem",
                                  border: "1px solid #d1d5db",
                                }}
                              >
                                {availableOperators.map((op) => (
                                  <option key={op} value={op}>
                                    {op}
                                  </option>
                                ))}
                              </select>

                              {/* Enhanced Value Input with validation */}
                              <div style={{ flex: 1, position: "relative" }}>
                                <input
                                  type="text"
                                  value={condition.value}
                                  onChange={(e) =>
                                    updateCondition(
                                      index,
                                      "value",
                                      e.target.value
                                    )
                                  }
                                  placeholder={getPlaceholder()}
                                  style={{
                                    width: "100%",
                                    padding: "0.5rem",
                                    borderRadius: "0.25rem",
                                    border: `2px solid ${
                                      validation.isValid ? "#d1d5db" : "#ef4444"
                                    }`,
                                    backgroundColor: validation.isValid
                                      ? "white"
                                      : "#fef2f2",
                                  }}
                                />

                                {/* Enhanced Error Message Display */}
                                {validation.error && (
                                  <div
                                    style={{
                                      position: "absolute",
                                      bottom: "-22px",
                                      left: 0,
                                      fontSize: "0.75rem",
                                      color: "#ef4444",
                                      backgroundColor: "#fef2f2",
                                      padding: "2px 6px",
                                      borderRadius: "4px",
                                      border: "1px solid #fecaca",
                                      whiteSpace: "nowrap",
                                      zIndex: 10,
                                      maxWidth: "300px",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {validation.error}
                                  </div>
                                )}

                                {/* Helper text for valid inputs */}
                                {validation.isValid &&
                                  condition.value &&
                                  dataType && (
                                    <div
                                      style={{
                                        position: "absolute",
                                        bottom: "-18px",
                                        left: 0,
                                        fontSize: "0.75rem",
                                        color: "#10b981",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      ✓ Valid {dataType.toLowerCase()} value
                                    </div>
                                  )}
                              </div>

                              {/* Remove Button */}
                              <button
                                onClick={() => removeCondition(index)}
                                style={{
                                  padding: "0.5rem",
                                  color: "#ef4444",
                                  backgroundColor: "transparent",
                                  border: "none",
                                  cursor: "pointer",
                                }}
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Sort Section - Enhanced from DynamicDashboard */}
                {(selectedApi || editId) && (
                  <div style={{ marginBottom: "1.5rem" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "0.5rem",
                      }}
                    >
                      <h3 style={{ fontSize: "0.875rem", fontWeight: "600" }}>
                        Sorting
                      </h3>
                      <button
                        onClick={addOrderByField}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.25rem",
                          padding: "0.25rem 0.5rem",
                          backgroundColor: "#10b981",
                          color: "white",
                          borderRadius: "0.25rem",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        <Plus size={14} /> Add Sort Field
                      </button>
                    </div>

                    {orderByFields.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.5rem",
                        }}
                      >
                        {orderByFields.map((field, index) => {
                          const availableApis = getAvailableApisForDropdowns();

                          const currentColumns =
                            field.api === selectedApi
                              ? apiColumns
                              : joinApiColumns[field.api]?.columns || [];

                          return (
                            <div
                              key={`sort-${index}-${field.api}`}
                              style={{
                                display: "flex",
                                gap: "0.5rem",
                                alignItems: "center",
                              }}
                            >
                              {/* Table selection */}
                              <select
                                value={field.api || ""}
                                onChange={(e) => {
                                  const newApi = e.target.value;
                                  const updatedFields = [...orderByFields];
                                  updatedFields[index] = {
                                    ...updatedFields[index],
                                    api: newApi,
                                    column: "",
                                  };
                                  setOrderByFields(updatedFields);
                                }}
                                style={{
                                  width: "200px",
                                  padding: "0.5rem",
                                  borderRadius: "0.25rem",
                                  border: "1px solid #d1d5db",
                                }}
                              >
                                <option value="">Select Table</option>
                                {availableApis.map((api) => (
                                  <option key={`api-${api.id}`} value={api.id}>
                                    {api.name}
                                  </option>
                                ))}
                              </select>

                              {/* Column selection */}
                              <select
                                value={field.column || ""}
                                onChange={(e) => {
                                  const updatedFields = [...orderByFields];
                                  updatedFields[index].column = e.target.value;
                                  setOrderByFields(updatedFields);
                                }}
                                disabled={!field.api}
                                style={{
                                  flex: 1,
                                  padding: "0.5rem",
                                  borderRadius: "0.25rem",
                                  border: "1px solid #d1d5db",
                                }}
                              >
                                <option value="">Select column</option>
                                {getColumnsForDropdowns(field.api).map(
                                  (column) => (
                                    <option
                                      key={`col-${field.api}-${column}`}
                                      value={column}
                                    >
                                      {column}
                                    </option>
                                  )
                                )}
                              </select>

                              {/* Sort direction */}
                              <select
                                value={field.direction}
                                onChange={(e) => {
                                  const updatedFields = [...orderByFields];
                                  updatedFields[index].direction =
                                    e.target.value;
                                  setOrderByFields(updatedFields);
                                }}
                                style={{
                                  width: "120px",
                                  padding: "0.5rem",
                                  borderRadius: "0.25rem",
                                  border: "1px solid #d1d5db",
                                }}
                              >
                                <option value="ASC">Ascending</option>
                                <option value="DESC">Descending</option>
                              </select>

                              <button
                                onClick={() => {
                                  const updatedFields = [...orderByFields];
                                  updatedFields.splice(index, 1);
                                  setOrderByFields(updatedFields);
                                }}
                                style={{
                                  padding: "0.5rem",
                                  color: "#ef4444",
                                  backgroundColor: "transparent",
                                  border: "none",
                                  cursor: "pointer",
                                }}
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Aggregators Section - Enhanced from DynamicDashboard */}
                {(selectedApi || editId) && (
                  <div style={{ marginBottom: "1.5rem" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "0.5rem",
                      }}
                    >
                      <h3 style={{ fontSize: "0.875rem", fontWeight: "600" }}>
                        Aggregators
                      </h3>
                      <button
                        onClick={addAggregator}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.25rem",
                          padding: "0.25rem 0.5rem",
                          backgroundColor: "#f59e0b",
                          color: "white",
                          borderRadius: "0.25rem",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        <Plus size={14} />
                        Add Aggregator
                      </button>
                    </div>

                    {aggregators.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.5rem",
                        }}
                      >
                        {aggregators.map((aggregator, index) => {
                          const availableApis = getAvailableApisForDropdowns();

                          const currentColumns =
                            aggregator.api === selectedApi
                              ? apiColumns
                              : joinApiColumns[aggregator.api]?.columns || [];

                          return (
                            <div
                              key={`aggregator-${index}-${aggregator.api}`}
                              style={{
                                display: "flex",
                                gap: "0.5rem",
                                alignItems: "center",
                              }}
                            >
                              {/* Aggregation Function */}
                              <select
                                value={aggregator.function}
                                onChange={(e) =>
                                  updateAggregator(
                                    index,
                                    "function",
                                    e.target.value
                                  )
                                }
                                style={{
                                  width: "120px",
                                  padding: "0.5rem",
                                  borderRadius: "0.25rem",
                                  border: "1px solid #d1d5db",
                                }}
                              >
                                {aggregatorTypes.map((func) => (
                                  <option key={func} value={func}>
                                    {func}
                                  </option>
                                ))}
                              </select>

                              {/* Table selection */}
                              <select
                                value={aggregator.api || ""}
                                onChange={(e) =>
                                  updateAggregator(index, "api", e.target.value)
                                }
                                style={{
                                  width: "200px",
                                  padding: "0.5rem",
                                  borderRadius: "0.25rem",
                                  border: "1px solid #d1d5db",
                                }}
                              >
                                <option value="">Select Table</option>
                                {availableApis.map((api) => (
                                  <option
                                    key={`agg-api-${api.id}`}
                                    value={api.id}
                                  >
                                    {api.name}
                                  </option>
                                ))}
                              </select>

                              {/* Column selection */}
                              <select
                                value={aggregator.column}
                                onChange={(e) =>
                                  updateAggregator(
                                    index,
                                    "column",
                                    e.target.value
                                  )
                                }
                                disabled={!aggregator.api}
                                style={{
                                  flex: 1,
                                  padding: "0.5rem",
                                  borderRadius: "0.25rem",
                                  border: "1px solid #d1d5db",
                                }}
                              >
                                <option value="">Select column</option>
                                {getColumnsForDropdowns(aggregator.api).map(
                                  (column) => (
                                    <option
                                      key={`agg-col-${aggregator.api}-${column}`}
                                      value={column}
                                    >
                                      {column}
                                    </option>
                                  )
                                )}
                              </select>

                              {/* Remove button */}
                              <button
                                onClick={() => removeAggregator(index)}
                                style={{
                                  padding: "0.5rem",
                                  color: "#ef4444",
                                  backgroundColor: "transparent",
                                  border: "none",
                                  cursor: "pointer",
                                }}
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Group By Section - Enhanced from DynamicDashboard */}
                {(selectedApi || editId) && (
                  <div style={{ marginBottom: "1.5rem" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "0.5rem",
                      }}
                    >
                      <h3 style={{ fontSize: "0.875rem", fontWeight: "600" }}>
                        Group By
                        <span
                          style={{
                            fontSize: "0.75rem",
                            color: "#4b5563",
                            marginLeft: "0.5rem",
                            fontWeight: "normal",
                          }}
                        >
                          (Required when using aggregators)
                        </span>
                      </h3>
                      <button
                        onClick={addGroupByField}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.25rem",
                          padding: "0.25rem 0.5rem",
                          backgroundColor: "#8b5cf6",
                          color: "white",
                          borderRadius: "0.25rem",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        <Plus size={14} /> Add Group Field
                      </button>
                    </div>

                    {groupByFields.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.5rem",
                        }}
                      >
                        {groupByFields.map((field, index) => {
                          const availableApis = getAvailableApisForDropdowns();

                          const currentColumns =
                            field.api === selectedApi
                              ? apiColumns
                              : joinApiColumns[field.api]?.columns || [];

                          return (
                            <div
                              key={`group-${index}-${field.api}`}
                              style={{
                                display: "flex",
                                gap: "0.5rem",
                                alignItems: "center",
                              }}
                            >
                              {/* Table selection */}
                              <select
                                value={field.api || ""}
                                onChange={(e) => {
                                  const newApi = e.target.value;
                                  const updatedFields = [...groupByFields];
                                  updatedFields[index] = {
                                    ...updatedFields[index],
                                    api: newApi,
                                    column: "",
                                  };
                                  setGroupByFields(updatedFields);
                                }}
                                style={{
                                  width: "200px",
                                  padding: "0.5rem",
                                  borderRadius: "0.25rem",
                                  border: "1px solid #d1d5db",
                                }}
                              >
                                <option value="">Select Table</option>
                                {availableApis.map((api) => (
                                  <option
                                    key={`group-api-${api.id}`}
                                    value={api.id}
                                  >
                                    {api.name}
                                  </option>
                                ))}
                              </select>

                              {/* Column selection */}
                              <select
                                value={field.column || ""}
                                onChange={(e) => {
                                  const updatedFields = [...groupByFields];
                                  updatedFields[index].column = e.target.value;
                                  setGroupByFields(updatedFields);
                                }}
                                disabled={!field.api}
                                style={{
                                  flex: 1,
                                  padding: "0.5rem",
                                  borderRadius: "0.25rem",
                                  border: "1px solid #d1d5db",
                                }}
                              >
                                <option value="">Select column</option>
                                {getColumnsForDropdowns(field.api).map(
                                  (column) => (
                                    <option
                                      key={`group-col-${field.api}-${column}`}
                                      value={column}
                                    >
                                      {column}
                                    </option>
                                  )
                                )}
                              </select>

                              <button
                                onClick={() => {
                                  const updatedFields = [...groupByFields];
                                  updatedFields.splice(index, 1);
                                  setGroupByFields(updatedFields);
                                }}
                                style={{
                                  padding: "0.5rem",
                                  color: "#ef4444",
                                  backgroundColor: "transparent",
                                  border: "none",
                                  cursor: "pointer",
                                }}
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div style={{ display: "flex", gap: "0.75rem" }}>
                  <button
                    onClick={buildJsonQuery}
                    disabled={!isValidQuery || !selectedApi}
                    style={{
                      padding: "0.5rem 1rem",
                      backgroundColor:
                        !isValidQuery || !selectedApi ? "#9ca3af" : "#1a7a73",
                      color: "white",
                      borderRadius: "0.5rem",
                      border: "none",
                      cursor:
                        !isValidQuery || !selectedApi
                          ? "not-allowed"
                          : "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <Play size={16} />
                    Build Query
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Results Section */}
        <div style={{ display: "flex", gap: "1.5rem", marginBottom: "1.5rem" }}>
          {/* JSON Query Panel */}
          <div
            style={{
              flex: 1,
              backgroundColor: "white",
              borderRadius: "0.75rem",
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "1rem",
                borderBottom: "1px solid #e5e7eb",
                backgroundColor: "#f9fafb",
              }}
            >
              <h3
                style={{
                  fontSize: "1rem",
                  fontWeight: "600",
                  color: "#1f2937",
                }}
              >
                Generated Query
              </h3>
            </div>
            <div
              style={{
                padding: "1rem",
                height: "400px",
                overflowY: "auto",
                backgroundColor: "#111827",
              }}
            >
              {jsonQuery ? (
                <>
                  <pre
                    style={{
                      color: "#f3f4f6",
                      fontSize: "0.875rem",
                      fontFamily: "monospace",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      margin: 0,
                    }}
                  >
                    {jsonQuery}
                  </pre>
                  <button
                    onClick={callNiFiAPI}
                    disabled={isExecuting || !jsonQuery || !isValidQuery}
                    style={{
                      marginTop: "1rem",
                      backgroundColor:
                        isExecuting || !jsonQuery || !isValidQuery
                          ? "#6b7280"
                          : "#10b981",
                      color: "white",
                      borderRadius: "0.5rem",
                      padding: "0.5rem 1rem",
                      fontWeight: "500",
                      fontSize: "0.875rem",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      border: "none",
                      cursor:
                        isExecuting || !jsonQuery || !isValidQuery
                          ? "not-allowed"
                          : "pointer",
                    }}
                  >
                    {isExecuting ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Executing...
                      </>
                    ) : (
                      <>
                        <Play size={16} />
                        Execute Query
                      </>
                    )}
                  </button>
                </>
              ) : (
                <div
                  style={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#6b7280",
                  }}
                >
                  <p>No query generated yet. Build a query first.</p>
                </div>
              )}
            </div>
          </div>

          {/* API Response Panel */}
          <div
            style={{
              flex: 1,
              backgroundColor: "white",
              borderRadius: "0.75rem",
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "1rem",
                borderBottom: "1px solid #e5e7eb",
                backgroundColor: "#f9fafb",
              }}
            >
              <h3
                style={{
                  fontSize: "1rem",
                  fontWeight: "600",
                  color: "#1f2937",
                }}
              >
                API Response
              </h3>
            </div>
            <div
              style={{ padding: "1rem", height: "400px", overflowY: "auto" }}
            >
              {jsonResponse ? (
                <pre
                  style={{
                    fontSize: "0.875rem",
                    fontFamily: "monospace",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    margin: 0,
                  }}
                >
                  {JSON.stringify(jsonResponse, null, 2)}
                </pre>
              ) : (
                <div
                  style={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#6b7280",
                  }}
                >
                  <p>No response yet. Execute a query to see results.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() =>
          setConfirmModal({
            isOpen: false,
            title: "",
            message: "",
            onConfirm: null,
          })
        }
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
      />
      {/* Toast Container */}
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default ApiBuilder;
